(() => {
  'use strict';
  const config = window.MG_CONFIG || {};
  const routes = config.routes || { root: '/', all: '/collections/all', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', contact: '/' };
  const bikes = config.bikes || {};
  const bikePhotos = config.bikePhotos || [];
  const categories = config.categories || [];
  const brandNames = Object.keys(bikes);
  const shopName = config.shopName || '';
  const seoTitle = document.title; // The server-rendered search title; kept when no filter is applied.
  // Product type is the category; anything that is not one of the listed categories (a typo, a new type) counts as the last one, Others.
  const categoryOf = type => categories.find(c => c.toLowerCase() === String(type || '').trim().toLowerCase()) || categories[categories.length - 1] || type;
  const toProduct = p => ({ ...p, brand: brandNames.includes(p.vendor) ? p.vendor : 'Universal', category: categoryOf(p.category), models: Array.isArray(p.models) ? p.models : [] });
  const products = (window.MG_PRODUCTS || []).map(toProduct);
  // Models tagged on products (model:Bullet 350) join their brand's list, after the ones listed in mg-config.
  function addTaggedBikes() {
    products.forEach(p => {
      if (!Object.hasOwn(bikes, p.brand)) return;
      p.models.forEach(model => { if (model && !bikes[p.brand].includes(model)) bikes[p.brand].push(model); });
    });
  }
  addTaggedBikes();
  const body = document.body;
  const template = body.dataset.template || '';
  const onBikesPage = template === 'collection' && body.dataset.templateSuffix === 'bikes';
  const onShopPage = template === 'collection' && !onBikesPage;
  const onProductPage = template === 'product';
  const onCartPage = template === 'cart';
  const siteHeader = document.getElementById('site-header');
  const announcement = document.querySelector('.announcement');

  // Read both heights before writing, and only write on change, so this never forces an extra layout.
  const syncHeaderHeight = () => {
    if (!siteHeader) return;
    const header = `${siteHeader.getBoundingClientRect().height}px`;
    const bar = `${announcement?.getBoundingClientRect().height || 0}px`;
    const style = document.documentElement.style;
    if (style.getPropertyValue('--sticky-header-height') !== header) style.setProperty('--sticky-header-height', header);
    if (style.getPropertyValue('--announcement-height') !== bar) style.setProperty('--announcement-height', bar);
  };
  // The resize observer measures once after the first layout, so no synchronous measurement is needed at start-up.
  if ('ResizeObserver' in window && siteHeader) {
    const headerObserver = new ResizeObserver(syncHeaderHeight);
    headerObserver.observe(siteHeader);
    if (announcement) headerObserver.observe(announcement);
  } else {
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);
  }
  const syncDialogViewport = () => {
    const viewport = window.visualViewport;
    if (viewport && viewport.scale !== 1) return;
    const height = viewport?.height || window.innerHeight;
    const top = `${viewport?.offsetTop || 0}px`;
    const root = document.documentElement;
    if (root.style.getPropertyValue('--dialog-viewport-height') !== `${height}px`) root.style.setProperty('--dialog-viewport-height', `${height}px`);
    if (root.style.getPropertyValue('--dialog-viewport-top') !== top) root.style.setProperty('--dialog-viewport-top', top);
    if (root.hasAttribute('data-compact-viewport') !== height < 480) root.toggleAttribute('data-compact-viewport', height < 480);
  };
  syncDialogViewport();
  window.addEventListener('resize', syncDialogViewport, { passive: true });
  window.visualViewport?.addEventListener('resize', syncDialogViewport, { passive: true });
  window.visualViewport?.addEventListener('scroll', syncDialogViewport, { passive: true });

  const money = value => new Intl.NumberFormat('en-IN', { style: 'currency', currency: config.currency || 'INR', maximumFractionDigits: 0 }).format(value);
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const responsiveCopy = (desktop, mobile) => `<span class="desktop-copy">${esc(desktop)}</span><span class="mobile-copy">${esc(mobile)}</span>`;
  const icon = name => `<svg class="icon" aria-hidden="true"><use href="#i-${name}"></use></svg>`;
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const segMemory = new Map();
  function syncSegments(root = document) {
    root.querySelectorAll('[data-seg]').forEach(group => {
      group.classList.add('has-seg');
      let indicator = group.querySelector(':scope > .seg-indicator');
      if (!indicator) { indicator = document.createElement('span'); indicator.className = 'seg-indicator'; indicator.setAttribute('aria-hidden', 'true'); group.prepend(indicator); }
      const active = group.querySelector(':scope > [aria-pressed="true"], :scope > [aria-current="true"]');
      if (!active) { indicator.style.opacity = '0'; segMemory.delete(group.dataset.seg); return; }
      const target = { x: active.offsetLeft, y: active.offsetTop, w: active.offsetWidth, h: active.offsetHeight };
      const place = r => { indicator.style.width = `${r.w}px`; indicator.style.height = `${r.h}px`; indicator.style.transform = `translate(${r.x}px, ${r.y}px)`; };
      const previous = segMemory.get(group.dataset.seg);
      indicator.style.opacity = '1';
      if (previous && !reduceMotion()) { indicator.style.transition = 'none'; place(previous); void indicator.offsetWidth; indicator.style.transition = ''; }
      place(target);
      segMemory.set(group.dataset.seg, target);
    });
  }
  function animateIn(container) {
    if (!container || reduceMotion()) return;
    [...container.children].slice(0, 12).forEach((card, i) => { card.style.setProperty('--i', i); card.classList.remove('anim-in'); void card.offsetWidth; card.classList.add('anim-in'); });
  }
  const motionOn = () => document.documentElement.classList.contains('motion');
  const revealSelector = '.section-head, .product-card, .category-tile, .part-type, .ride-guide-panel, .help-band .container, .benefit, .explore-tile, .model-choice-explore, .bike-model-card, .pdp-accordions, .cart-page-summary, .footer-grid > *, .footer-bottom, .mg-page .rte';
  let revealObserver = null;
  function observeReveal(root = document) {
    if (!motionOn() || !('IntersectionObserver' in window)) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(entries => {
        let order = 0;
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          revealObserver.unobserve(el);
          el.style.transitionDelay = `${Math.min(order++, 6) * 70}ms`;
          el.classList.add('is-revealed');
          setTimeout(() => { el.classList.remove('reveal', 'is-revealed'); el.style.transitionDelay = ''; }, 1200);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.1 });
    }
    root.querySelectorAll(revealSelector).forEach(el => {
      if (el.dataset.reveal) return;
      el.dataset.reveal = '1';
      const r = el.getBoundingClientRect();
      if (r.bottom > 0 && r.top < innerHeight) return;
      el.classList.add('reveal');
      revealObserver.observe(el);
    });
  }
  const navProgress = document.createElement('div');
  navProgress.className = 'nav-progress';
  navProgress.setAttribute('aria-hidden', 'true');
  document.body.appendChild(navProgress);
  function startNavProgress() { navProgress.classList.remove('is-loading'); void navProgress.offsetWidth; navProgress.classList.add('is-loading'); }
  window.addEventListener('pageshow', () => navProgress.classList.remove('is-loading'));
  const findProduct = id => products.find(p => p.id === id);
  const blurSrc = src => /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$1120') : src;
  const photoFills = image => Boolean(image && image.width >= 500 && image.height && Math.abs(image.width / image.height - 1) <= 0.05);
  const percentOff = (price, compareAt) => compareAt > price ? Math.round((compareAt - price) / compareAt * 100) : 0;
  const offBadge = (price, compareAt, className = 'price-off') => { const off = percentOff(price, compareAt); return off >= 1 ? `<span class="${className}">${off}% OFF</span>` : ''; };
  const imageFor = p => p.image;
  // Shopify CDN images take a width parameter, so offer a few sizes and let the browser pick the smallest that fits.
  const imageSrcset = src => /[?&]width=\d+/.test(src) ? [360, 540, 720].map(w => `${src.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(', ') : '';
  const imageTag = (image, alt, extra = '') => {
    if (!image) return '';
    const srcset = imageSrcset(image.src);
    return `<img class="od-media" src="${esc(image.src)}"${srcset ? ` srcset="${esc(srcset)}" sizes="(min-width: 1024px) 300px, 50vw"` : ''} width="${image.width}" height="${image.height}" alt="${esc(alt)}" loading="${extra.includes('fetchpriority="high"') ? 'eager' : 'lazy'}" decoding="async" ${extra}>`;
  };
  const storageGet = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } };
  const storageSet = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* The current session keeps its state when storage is unavailable. */ } };
  const readJSON = id => { try { return JSON.parse(document.getElementById(id)?.textContent || 'null'); } catch { return null; } };
  const rootURL = hash => `${routes.root}${routes.root.endsWith('/') ? '' : '/'}${hash ? `#${hash}` : ''}`;

  const bikePhoto = (brand, model) => bikePhotos.find(photo => photo.brand === brand && photo.model === model);
  const bikeSelectionURL = brand => Object.hasOwn(bikes, brand) ? `${routes.all}?view=bikes&brand=${encodeURIComponent(brand)}` : rootURL('bike-finder');
  const selectionBrand = (search = location.search) => { const brand = new URLSearchParams(search).get('brand') || ''; return Object.hasOwn(bikes, brand) ? brand : ''; };
  const selectionModel = (search = location.search) => { const model = new URLSearchParams(search).get('model') || ''; return bikes[selectionBrand(search)]?.includes(model) ? model : ''; };

  const savedBike = storageGet('motogarage-bike-v1', {});
  let bike = savedBike && typeof savedBike === 'object' && Object.hasOwn(bikes, savedBike.brand) ? { brand: savedBike.brand, model: bikes[savedBike.brand].includes(savedBike.model) ? savedBike.model : '' } : { brand: '', model: '' };
  const shopPath = document.querySelector('[data-shop-root]')?.dataset.collectionUrl || routes.all;
  // A bike collection (Bike brand / Bike model collection metafields) lists that bike like the filtered shop, at its own address.
  const collectionBike = (() => {
    const data = document.querySelector('[data-shop-root]')?.dataset || {};
    return data.bikeBrand && Object.hasOwn(bikes, data.bikeBrand) ? { brand: data.bikeBrand, model: bikes[data.bikeBrand].includes(data.bikeModel) ? data.bikeModel : '' } : null;
  })();
  const storedShop = storageGet('motogarage-last-shop-v1', '');
  const validShop = value => typeof value === 'string' && value.startsWith('/collections/') && !value.includes('view=');
  let lastShop = validShop(storedShop) ? storedShop : routes.all;
  let cart = readJSON('mg-cart-state') || { items: [], item_count: 0, total_price: 0 };
  let cartChanged = false;
  let lightboxState = null;
  let bikeReturnCart = false;
  let purchaseResizeObserver = null;
  let pendingFocus = null;
  let preserveScroll = null;
  let lastRemoved = null;
  let toastTimer = null;
  let searchContext = null;
  let firstRender = true;
  const showcasePageSize = 8;
  let showcaseBrand = '';

  const queryOf = url => { try { return new URL(url, location.origin).search; } catch { return ''; } };
  const getFilters = (search = location.search) => {
    const params = new URLSearchParams(search);
    const fallback = onShopPage && collectionBike || bike;
    let brand = params.has('brand') ? params.get('brand') : fallback.brand;
    if (!Object.hasOwn(bikes, brand)) brand = '';
    let model = params.has('model') ? params.get('model') : (brand === fallback.brand ? fallback.model : '');
    if (!brand && model) brand = Object.keys(bikes).find(b => bikes[b].includes(model)) || '';
    if (!bikes[brand]?.includes(model)) model = '';
    return { brand, model, category: categories.includes(params.get('category')) ? params.get('category') : '', q: params.get('q') || '', sort: ['featured', 'price-low', 'price-high', 'name'].includes(params.get('sort')) ? params.get('sort') : 'featured' };
  };
  const bikePages = config.bikePages || {};
  const shopURL = (updates = {}, base = { brand: bike.brand, model: bike.model }, path = onShopPage ? shopPath : routes.all) => {
    const f = { ...base, ...updates };
    // A bike with its own collection page links there; the page itself supplies the brand and model.
    const page = f.model && bikePages[`${f.brand}|${f.model}`];
    const onPage = Boolean(page && (path === routes.all || collectionBike));
    if (onPage) path = page;
    else if (collectionBike && path === shopPath) path = routes.all;
    const params = new URLSearchParams();
    for (const k of ['brand', 'model', 'category', 'q', 'sort']) if (f[k] || (!onPage && ['brand', 'model'].includes(k))) params.set(k, f[k] || '');
    if (onPage) { params.delete('brand'); params.delete('model'); }
    const query = params.toString();
    return query ? `${path}?${query}` : path;
  };
  const setBike = (brand, model) => {
    bike = { brand, model };
    storageSet('motogarage-bike-v1', bike);
  };
  const rememberShop = url => { if (validShop(url)) { lastShop = url; storageSet('motogarage-last-shop-v1', lastShop); } };
  const currentPath = () => `${location.pathname}${location.search}`;

  function browseContext() {
    if (onShopPage) return getFilters();
    if (onBikesPage) return { brand: selectionBrand(), model: selectionModel(), category: '', q: '', sort: 'featured' };
    const base = getFilters(queryOf(lastShop));
    if (onProductPage && productPage) {
      const product = productPage;
      if (product.brand !== 'Universal') {
        const sameBrand = base.brand === product.brand;
        return { ...base, brand: product.brand, model: sameBrand ? base.model : (bike.brand === product.brand ? bike.model : ''), category: sameBrand ? base.category : '', q: '' };
      }
      return base;
    }
    if (onCartPage) return base;
    return { brand: bike.brand, model: bike.model, category: '', q: '', sort: 'featured' };
  }
  function updateHeader() {
    const count = cart.item_count || 0;
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      const before = Number(el.textContent) || 0;
      el.textContent = count; el.hidden = !count;
      if (count > before && !firstRender && !reduceMotion()) { el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump'); }
    });
    const context = browseContext();
    document.querySelectorAll('[data-brand-link]').forEach(el => {
      if ((onShopPage || onBikesPage || onProductPage) && el.dataset.brandLink === context.brand) el.setAttribute('aria-current', 'page');
      else el.removeAttribute('aria-current');
    });
    document.querySelectorAll('[data-cart-trigger]').forEach(el => el.setAttribute('aria-label', `Open cart, ${count} ${count === 1 ? 'item' : 'items'}`));
    renderIsland();
  }
  /* Checkout island: a floating cart summary with a checkout button. It stays tucked away while the
     home hero's brand cards are on screen, so it never covers the first choice. */
  const heroBrands = document.querySelector('.hero-brand-list');
  if (heroBrands && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => document.body.classList.toggle('hero-in-view', entry.isIntersecting)).observe(heroBrands);
  }
  let islandTimer = null;
  function renderIsland(added) {
    const island = document.getElementById('cart-island');
    if (!island) return;
    const count = cart.item_count || 0;
    island.hidden = !count;
    document.documentElement.style.setProperty('--island-space', count ? '84px' : '0px');
    if (!count) return;
    island.querySelector('[data-island-count]').textContent = count;
    island.querySelector('[data-island-total]').textContent = money((cart.total_price || 0) / 100);
    const label = island.querySelector('[data-island-label]');
    const summary = `${count} ${count === 1 ? 'item' : 'items'}${config.freeDelivery ? ' · Free delivery' : ''}`;
    const summaryHTML = `${count} ${count === 1 ? 'item' : 'items'}${config.freeDelivery ? '<span class="island-free"> · Free delivery</span>' : ''}`;
    clearTimeout(islandTimer);
    if (added) {
      label.textContent = `${added} added`;
      island.classList.add('is-added');
      islandTimer = setTimeout(() => { label.innerHTML = summaryHTML; island.classList.remove('is-added'); }, 2600);
    } else if (!island.classList.contains('is-added')) label.innerHTML = summaryHTML;
    island.querySelector('.cart-island-summary').setAttribute('aria-label', `View cart, ${summary}, ${money((cart.total_price || 0) / 100)}`);
    if (added && count > 1 && !reduceMotion()) { island.classList.remove('pop'); void island.offsetWidth; island.classList.add('pop'); }
  }
  function closeDialogs() { document.querySelectorAll('dialog[open]').forEach(d => d.close()); }
  function openDialog(id) {
    rememberPurchase();
    if (id === 'bike-dialog') bikeReturnCart = !!document.getElementById('cart-dialog')?.open;
    const d = document.getElementById(id);
    if (!d) return;
    closeDialogs();
    if (id === 'cart-dialog') renderCart();
    if (id === 'bike-dialog') d.querySelector('.dialog-body').innerHTML = `<p class="muted">Choose your model to find the accessories listed for it.</p>${finderForm('dialog')}`;
    d.showModal();
    body.classList.add('dialog-open');
    syncPurchaseBar();
    if (id === 'search-dialog') {
      const context = browseContext();
      document.getElementById('search-input').value = context.q;
      setSearchScope(context);
      d.querySelector('input').focus();
    }
  }
  function closeDialog(d) {
    if (!d?.open) return;
    if (reduceMotion() || d.classList.contains('is-closing')) { d.classList.remove('is-closing'); d.close(); return; }
    d.classList.add('is-closing');
    setTimeout(() => { d.classList.remove('is-closing'); d.close(); }, 170);
  }
  document.querySelectorAll('dialog').forEach(d => {
    d.addEventListener('cancel', e => { e.preventDefault(); closeDialog(d); });
    d.addEventListener('close', () => {
      if (!document.querySelector('dialog[open]')) body.classList.remove('dialog-open');
      syncPurchaseBar();
    });
    d.addEventListener('click', e => {
      if (e.target !== d) return;
      const r = d.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeDialog(d);
    });
  });
  function notify(message, showCart = false, undo = false) {
    const el = document.getElementById('toast');
    if (!el) return;
    const host = document.querySelector('dialog[open]') || document.body;
    if (el.parentElement !== host) host.appendChild(el);
    clearTimeout(toastTimer);
    el.innerHTML = `${icon('check')}<span>${esc(message)}</span>${showCart ? '<button type="button" data-action="cart">View cart</button>' : ''}${undo ? '<button type="button" data-action="cart-undo">Undo</button>' : ''}`;
    el.hidden = false;
    toastTimer = setTimeout(() => { el.hidden = true; }, 5000);
  }
  function navigate(url, options = {}) {
    const target = new URL(url, location.href);
    closeDialogs();
    const sameDocument = target.pathname === location.pathname && target.origin === location.origin;
    const isBikesURL = target.searchParams.get('view') === 'bikes';
    if (sameDocument && ((onShopPage && !isBikesURL) || (onBikesPage && isBikesURL))) {
      pendingFocus = options.focusId || null;
      preserveScroll = options.preserveScroll ? window.scrollY : null;
      if (target.href !== location.href) history.pushState({}, '', target);
      renderPage();
      return;
    }
    startNavProgress();
    location.href = target.href;
  }
  function finderForm(prefix) {
    const context = bike.brand ? bike : browseContext();
    return `<form class="od-stack bike-form" data-form="bike" novalidate>
      <div class="field od-field"><label for="${prefix}-brand">Bike brand</label><select id="${prefix}-brand" name="brand" required aria-describedby="${prefix}-brand-error"><option value="">Choose your brand</option>${Object.keys(bikes).map(b => `<option ${context.brand === b ? 'selected' : ''}>${esc(b)}</option>`).join('')}</select><span class="field-error" id="${prefix}-brand-error" hidden></span></div>
      <div class="field od-field"><label for="${prefix}-model">Bike model</label><select id="${prefix}-model" name="model" ${context.brand ? '' : 'disabled'}><option value="">Choose a model</option>${(bikes[context.brand] || []).map(m => `<option ${context.model === m ? 'selected' : ''}>${esc(m)}</option>`).join('')}</select></div>
      <button class="btn btn-dark" type="submit">${onProductPage || onCartPage ? 'Save my bike' : 'Find my accessories'}</button>
    </form>`;
  }

  function fitLabel(p) { return p.models.length ? p.models.join(' / ') : 'Confirm bike fitment'; }
  function productCard(p, presentation) {
    const im = imageFor(p);
    const linked = presentation === 'linked';
    const quick = presentation === 'quick' && p.available && p.variantCount === 1 && p.variantId;
    const outerTag = linked ? 'a' : 'article';
    const innerTag = linked ? 'span' : 'a';
    const href = p.url;
    const titleId = `showcase-name-${p.id}`;
    const fitId = `showcase-fit-${p.id}`;
    return `<${outerTag} class="product-card od-tile${linked ? ' product-card-link' : ''}" data-card-id="${esc(p.id)}" data-card-kind="${linked ? 'linked' : presentation === 'quick' ? 'quick' : 'plain'}"${linked ? ` href="${esc(href)}" aria-labelledby="${esc(titleId)}" aria-describedby="${esc(fitId)}"` : ''}>
      <div class="product-photo${photoFills(im) ? ' product-photo-fill' : im ? ' has-blur' : ''}"${im && !photoFills(im) ? ` style="--photo-bg:url('${esc(blurSrc(im.src))}')"` : ''}>${offBadge(p.price, p.compareAt, 'photo-off')}${linked ? `<div class="product-image-frame">${imageTag(im, '')}</div>` : `<a href="${esc(href)}" tabindex="-1" aria-hidden="true">${imageTag(im, p.shortName)}</a>`}</div>
      <div class="product-info"><span class="product-category">${esc(p.category)}</span><${innerTag} class="product-title od-clamp-2" ${linked ? `id="${esc(titleId)}"` : `href="${esc(href)}"`}>${esc(p.shortName)}</${innerTag}><span class="product-fit od-clamp-2"${linked ? ` id="${esc(fitId)}"` : ''}>${esc(fitLabel(p))}</span>${p.brand === 'Universal' ? `<span class="product-badge">${responsiveCopy('Universal · Check fitment', 'Check fitment')}</span>` : ''}
      <div class="product-price-row"><div class="od-field"><span class="od-nowrap"><span class="price">${money(p.price)}</span>${p.compareAt > p.price ? `<s class="compare-price">${money(p.compareAt)}</s>` : ''}</span>${!p.available ? '<span class="availability-label">Unavailable</span>' : ''}</div>${quick ? `<button type="button" class="card-add" data-action="quick-add" data-product="${esc(p.id)}" aria-label="Add ${esc(p.shortName)} to cart" title="Add to cart">${icon('bag-plus')}<span class="card-add-label">Add</span></button>` : ''}<${innerTag} class="product-open"${linked ? ' aria-hidden="true"' : ` href="${esc(href)}" aria-label="View ${esc(p.shortName)}"`}><span class="mobile-copy">View accessory</span>${icon('arrow')}</${innerTag}></div></div>
    </${outerTag}>`;
  }

  /* Home page */
  const showcaseRoot = document.querySelector('[data-showcase-body]');
  const showcaseOrder = (() => {
    if (!showcaseRoot) return [];
    try { return JSON.parse(showcaseRoot.dataset.handles || '[]').map(findProduct).filter(Boolean); } catch { return []; }
  })();
  // Products keep the section collection's order (best selling); "All" includes universal parts.
  function showcaseProducts(brand = showcaseBrand) {
    return brand ? showcaseOrder.filter(p => p.brand === brand) : showcaseOrder;
  }
  function homeShowcase() {
    const pool = showcaseProducts();
    const picks = pool.slice(0, showcasePageSize);
    const context = shopURL({ brand: showcaseBrand, model: '', category: '', q: '' });
    const viewAll = shopURL({ brand: showcaseBrand, model: '', category: '', q: '' }, {}, showcaseRoot.dataset.collectionUrl || routes.all);
    const filters = ['', ...brandNames];
    const note = showcaseRoot.dataset.note || '';
    return `<div class="showcase-toolbar od-row"><div class="showcase-switch od-rail" data-seg="showcase" role="group" aria-label="Filter accessories by brand">${filters.map((brand, i) => {
        const count = showcaseProducts(brand).length;
        return `<button class="showcase-choice" id="showcase-brand-${i}" type="button" data-action="home-showcase" data-brand="${esc(brand)}" aria-pressed="${brand === showcaseBrand}" aria-controls="showcase-results"><span>${esc(brand || 'All')}</span> <span class="showcase-count">${count}</span><span class="sr-only"> accessories</span></button>`;
      }).join('')}</div><span class="showcase-scope">${showcaseBrand ? 'All ' + esc(showcaseBrand) + ' models' : 'Every brand. Every model.'}</span></div>
      <div id="showcase-results" class="od-grid home-products showcase-products" data-browse-shop="${esc(context)}">${picks.map(p => productCard(p, 'quick')).join('')}</div>
      ${pool.length ? '' : '<div class="empty-state"><h3>No accessories in this range yet.</h3><p>Choose another brand to explore the available accessories.</p></div>'}
      <div class="showcase-footer od-stack"><p class="showcase-status" id="showcase-status" role="status" aria-live="polite" aria-atomic="true">Top ${picks.length} of ${pool.length} accessories${showcaseBrand ? ' for ' + esc(showcaseBrand) : ''}</p>${picks.length < pool.length ? `<a class="btn btn-outline showcase-view-all" href="${esc(viewAll)}">View all best sellers ${icon('arrow')}</a>` : ''}</div>
      ${note ? `<p class="showcase-note">${esc(note)}</p>` : ''}`;
  }
  function refreshHomeShowcase(focusId, firstNewIndex) {
    if (!showcaseRoot) return;
    showcaseRoot.innerHTML = homeShowcase();
    syncSegments(showcaseRoot);
    if (focusId !== undefined || firstNewIndex !== undefined) animateIn(document.getElementById('showcase-results'));
    const viewLink = showcaseRoot.closest('section')?.querySelector('.section-head .text-link');
    if (viewLink) viewLink.href = shopURL({ brand: showcaseBrand, model: '', category: '', q: '' });
    if (focusId === undefined && firstNewIndex === undefined) return;
    const focusTarget = firstNewIndex === undefined ? document.getElementById(focusId) : document.getElementById('showcase-results').children[firstNewIndex];
    focusTarget?.focus({ preventScroll: true });
    if (firstNewIndex !== undefined) focusTarget?.closest('.product-card')?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    const status = document.getElementById('showcase-status');
    const message = status.textContent;
    status.textContent = '';
    requestAnimationFrame(() => { if (status.isConnected) status.textContent = message; });
  }
  function homeCategories() {
    const section = document.querySelector('[data-home-categories]');
    if (!section) return;
    const context = { brand: bike.brand, model: bike.model, q: '', sort: 'featured' };
    const descriptions = config.homeCategoryDescriptions || {};
    let customImages = {};
    try { customImages = JSON.parse(section.dataset.categoryImages || '{}') || {}; } catch (e) {}
    const contextLine = section.querySelector('[data-categories-context]');
    if (contextLine) contextLine.innerHTML = responsiveCopy(bike.brand ? `Accessories that fit your ${esc(bike.brand)}${bike.model ? ` ${esc(bike.model)}` : ''}, sorted by what they do.` : 'Every accessory we stock, sorted by what it does.', bike.brand ? `For your ${esc(bike.model || bike.brand)}.` : 'Find accessories by category.');
    const allLink = section.querySelector('[data-categories-all-link]');
    if (allLink) allLink.href = shopURL({ category: '' }, context);
    section.querySelector('.part-type-grid').innerHTML = categories.map(category => {
      const list = matchingProducts({ ...context, category });
      const example = customImages[category] || imageFor(list.find(p => p.image && p.available) || list.find(p => p.image) || {});
      const count = list.length ? `${list.length} ${list.length === 1 ? 'accessory' : 'accessories'}` : 'No accessories yet';
      return `<a class="part-type${list.length ? '' : ' is-empty'}" href="${esc(shopURL({ category }, context))}"><div class="part-type-media">${example ? imageTag(example, '') : ''}${list.length ? '' : '<span class="part-type-soon">Coming soon</span>'}<span class="part-type-count">${count}</span></div><div class="part-type-body"><div class="part-type-copy"><h3>${esc(category)}</h3><p>${esc(descriptions[category] || '')}</p></div><span class="part-type-go" aria-hidden="true">${icon('arrow')}</span></div></a>`;
    }).join('');
  }
  function rideGuide() {
    const contextRoot = document.querySelector('[data-ride-guide-context]');
    if (!contextRoot) return;
    const context = { brand: bike.brand, model: bike.model, q: '', sort: 'featured' };
    const brandEmpty = bike.brand && !products.some(p => p.brand === bike.brand);
    contextRoot.innerHTML = `${bike.brand ? `<p>${brandEmpty ? `No ${esc(bike.brand)} accessories listed yet.` : `Browsing for ${esc(bike.model || bike.brand)}.`}</p>` : ''}<button type="button" class="text-link nav-link" data-action="choose-bike">${bike.brand ? 'Change your bike' : 'Choose your bike'} ${icon('arrow')}</button>`;
    document.querySelectorAll('[data-ride-category]').forEach(link => { link.href = shopURL({ category: link.dataset.rideCategory }, context); });
  }
  function heroChooser() {
    const footer = document.querySelector('[data-hero-chooser-footer]');
    if (!footer) return;
    footer.innerHTML = `<a class="text-link" href="${esc(shopURL({ brand: '', model: '' }))}">Browse all accessories ${icon('arrow')}</a>`;
    // A returning rider's saved bike is the quickest way back in, so it sits above the brand cards.
    const saved = document.querySelector('[data-hero-saved]');
    if (!saved) return;
    saved.hidden = !bike.brand;
    const prompt = document.querySelector('[data-hero-prompt]');
    if (prompt) prompt.textContent = bike.brand ? 'Or pick a brand' : prompt.dataset.default;
    if (!bike.brand) { saved.innerHTML = ''; return; }
    const photo = bike.model ? bikePhoto(bike.brand, bike.model) : null;
    const n = products.filter(p => p.brand === bike.brand && (!bike.model || p.models.includes(bike.model))).length;
    saved.innerHTML = `<a class="hero-brand-card hero-saved-card" href="${esc(bike.model ? shopURL({ brand: bike.brand, model: bike.model, category: '', q: '' }, undefined, routes.all) : bikeSelectionURL(bike.brand))}">
      <div class="hero-brand-copy od-stack"><span class="eyebrow">YOUR BIKE</span><div class="hero-brand-heading od-row"><h3>${esc(bike.model || bike.brand)}</h3>${icon('arrow')}</div><p>${esc(bike.brand)} · ${n ? `${n} ${n === 1 ? 'accessory' : 'accessories'}` : 'No accessories listed yet'}</p></div>
      <div class="hero-brand-logo hero-saved-photo">${photo ? `<img class="od-media" src="${esc(photo.src)}" width="${photo.width}" height="${photo.height}" alt="" decoding="async">` : icon('bike')}</div>
    </a>`;
  }
  function renderHome() {
    // On first load the server-rendered cards are already correct; redrawing them would download every image again.
    if (showcaseRoot) { if (firstRender) syncSegments(showcaseRoot); else refreshHomeShowcase(); }
    if (!firstRender || bike.brand) homeCategories();
    rideGuide();
    heroChooser();
  }

  /* Catalogue filtering */
  const hasStock = p => p.available;
  function matchesQuery(p, query) {
    const text = `${p.title} ${p.shortName} ${p.category} ${p.brand} ${p.models.join(' ')}`.toLowerCase();
    return query.toLowerCase().trim().split(/\s+/).filter(Boolean).every(term => text.includes(term));
  }
  function sortProducts(list, sort) {
    return [...list].sort((a, b) => sort === 'price-low' ? a.price - b.price : sort === 'price-high' ? b.price - a.price : sort === 'name' ? a.title.localeCompare(b.title) : Number(hasStock(b)) - Number(hasStock(a)));
  }
  function matchingProducts(f, pool = products) {
    return sortProducts(pool.filter(p => (!f.brand || p.brand === f.brand) && (!f.model || p.models.includes(f.model)) && (!f.category || p.category === f.category) && matchesQuery(p, f.q || '')), f.sort);
  }
  function universalProducts(f, pool = products) {
    return sortProducts(pool.filter(p => p.brand === 'Universal' && (!f.category || p.category === f.category) && matchesQuery(p, f.q || '')), f.sort);
  }
  // The shop list for a chosen bike also holds the universal parts of that type, after the bike's own parts.
  function shopProducts(f, pool = products) {
    const list = matchingProducts(f, pool);
    return f.brand ? sortProducts([...list, ...universalProducts(f, pool)], f.sort) : list;
  }

  /* Bike search: matches "cb 350", "hness", "himalayan" or "re hunter" to model names. */
  const compact = value => value.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '');
  const brandAliases = { 'Royal Enfield': 're', 'Honda': '' };
  function bikeMatches(query, onlyBrand = '') {
    const terms = query.split(/\s+/).map(compact).filter(Boolean);
    if (!terms.length) return [];
    return Object.entries(bikes).flatMap(([brand, models]) => onlyBrand && brand !== onlyBrand ? [] : models.map(model => ({ brand, model })))
      .filter(({ brand, model }) => {
        const name = compact(`${brandAliases[brand] || ''} ${brand} ${model}`);
        return name.includes(terms.join('')) || terms.every(term => name.includes(term));
      });
  }
  function filterBikeCards(query) {
    const grid = document.getElementById('model-choice-grid');
    if (!grid) return;
    const matches = new Set(bikeMatches(query, grid.dataset.brand).map(b => b.model));
    const cards = [...grid.querySelectorAll('.bike-model-card')];
    let shown = 0;
    cards.forEach(card => { const show = !query.trim() || matches.has(card.dataset.bikeModel); card.hidden = !show; if (show) shown++; });
    const status = document.getElementById('bike-search-status');
    if (status) status.textContent = query.trim() ? `${shown} of ${cards.length} models` : '';
    const empty = document.getElementById('bike-search-empty');
    if (empty) {
      empty.hidden = shown > 0;
      empty.querySelector('[data-bike-query]').textContent = query.trim();
    }
  }

  /* Bike chooser pages */
  function bikeSelection(brand) {
    if (!Object.hasOwn(bikes, brand)) return `<section class="container section"><div class="empty-state"><h1>Choose your bike brand.</h1><p>Select a brand to see its bike models.</p><div class="empty-actions od-cluster">${Object.keys(bikes).map(name => `<a class="btn btn-outline" href="${esc(bikeSelectionURL(name))}">${esc(name)} ${icon('arrow')}</a>`).join('')}</div><a class="text-link" href="${esc(shopURL({ brand: '', model: '', category: '', q: '' }))}">Browse all accessories ${icon('arrow')}</a></div></section>`;
    const noListings = !products.some(p => p.brand === brand);
    return `<section class="container model-choice-page" aria-labelledby="model-choice-title">
      <nav class="breadcrumb" aria-label="Breadcrumb"><a href="${esc(rootURL())}">Home</a>${icon('chevron')}<a href="${esc(rootURL('bike-finder'))}">Brands</a>${icon('chevron')}<span aria-current="page">${esc(brand)}</span></nav>
      <header class="model-choice-header od-row"><div class="od-stack od-fill"><p class="eyebrow">${esc(brand)}</p><h1 id="model-choice-title">Which bike do you ride?</h1><p>${responsiveCopy('Choose your model to see the accessories listed for it.', 'Pick your model to see its accessories.')}</p></div><a class="text-link od-fixed" href="${esc(rootURL('bike-finder'))}">Change brand ${icon('arrow')}</a></header>
      ${noListings ? `<p class="model-choice-notice">No ${esc(brand)} accessories are listed yet. You can still select your bike to see universal accessories that fit it.</p>` : ''}
      ${bikes[brand].length > 6 ? `<div class="bike-search od-row"><label class="search-field bike-search-field">${icon('search')}<span class="sr-only">Search ${esc(brand)} models</span><input type="search" id="bike-search" placeholder="Search your model, e.g. ${esc(bikes[brand][Math.min(5, bikes[brand].length - 1)].split(' ')[0])}" autocomplete="off" autocapitalize="off" spellcheck="false" enterkeyhint="go" aria-controls="model-choice-grid" aria-describedby="bike-search-status"></label><p class="bike-search-status od-fixed" id="bike-search-status" role="status"></p></div>
      <div class="bike-search-empty" id="bike-search-empty" hidden><p><strong>No ${esc(brand)} model matches “<span data-bike-query></span>”.</strong> Check the spelling, or <button type="button" class="text-link" data-action="bike-search-clear">show all models</button>.</p></div>` : ''}
      <div class="model-choice-grid od-grid" id="model-choice-grid" data-brand="${esc(brand)}">${bikes[brand].map((model, i) => {
        const photo = bikePhoto(brand, model);
        const count = products.filter(p => p.brand === brand && p.models.includes(model)).length;
        const selected = bike.brand === brand && bike.model === model;
        return `<a class="bike-model-card od-stack" id="bike-choice-${i}" href="${esc(shopURL({ brand, model, category: '', q: '', sort: 'featured' }, undefined, routes.all))}" data-bike-brand="${esc(brand)}" data-bike-model="${esc(model)}" data-saved="${selected}" aria-labelledby="bike-name-${i}" aria-describedby="bike-count-${i}${selected ? ` bike-saved-${i}` : ''}">
          <div class="bike-model-media">${imageTag(photo, `${brand} ${model}, side profile`, i === 0 ? 'fetchpriority="high"' : '') || `<span class="bike-model-placeholder">${icon('bike')}</span>`}</div>
          <div class="bike-model-body od-stack"><div class="od-row"><h2 class="od-fill" id="bike-name-${i}">${esc(model)}</h2><span class="bike-model-arrow od-fixed" aria-hidden="true">${icon('arrow')}</span></div><div class="bike-model-meta od-cluster"><p id="bike-count-${i}">${count ? `${count} ${count === 1 ? 'accessory' : 'accessories'} listed` : 'No accessories listed yet'}</p>${selected ? `<span class="bike-saved od-row" id="bike-saved-${i}">${icon('check')}Saved bike</span>` : ''}</div></div>
        </a>`;
      }).join('')}</div>
      <section class="model-choice-explore" aria-labelledby="explore-title">
        <h2 class="model-choice-explore-title" id="explore-title">Prefer to explore everything?</h2>
        <nav class="model-choice-explore-links" aria-label="Browse without selecting a bike">
          <a class="explore-tile" href="${esc(rootURL('best-sellers'))}"><span class="explore-tile-copy"><strong>Best sellers</strong><span>Riders’ top picks</span></span>${icon('arrow')}</a>
          <a class="explore-tile" href="${esc(shopURL({ brand: '', model: '', category: '', q: '', sort: 'featured' }))}"><span class="explore-tile-copy"><strong>All accessories</strong><span>Full catalogue</span></span>${icon('arrow')}</a>
        </nav>
        <p class="model-choice-fitment">${icon('info')}<span>${responsiveCopy('Check mounting points and fitment on each product page.', 'Check fitment on each product page.')}</span></p>
      </section>
    </section>`;
  }
  function renderBikes() {
    const root = document.querySelector('[data-bikes-root]');
    if (!root) return;
    const brand = selectionBrand();
    const model = selectionModel();
    if (model) { setBike(brand, model); location.replace(shopURL({ brand, model, category: '', q: '', sort: 'featured' }, undefined, routes.all)); return; }
    root.innerHTML = bikeSelection(brand);
    document.title = brand ? `${brand} Parts by Bike Model | ${shopName}` : seoTitle;
  }

  /* Collection page */
  const shopRoot = document.querySelector('[data-shop-root]');
  const collectionHandles = (() => {
    if (!shopRoot || collectionBike) return null;
    try { const handles = JSON.parse(shopRoot.querySelector('[data-collection-handles]')?.textContent || 'null'); return Array.isArray(handles) ? handles : null; } catch { return null; }
  })();
  let collectionPool = collectionHandles ? collectionHandles.map(findProduct).filter(Boolean) : products;
  const collectionTitle = shopRoot?.dataset.collectionTitle || '';
  function shop() {
    const f = getFilters();
    const pool = collectionPool;
    const list = shopProducts(f, pool);
    const emptyBrand = Boolean(f.brand) && !pool.some(p => p.brand === f.brand);
    const title = f.model ? `${f.model} Accessories` : f.brand ? `${f.brand} Accessories` : collectionTitle || 'All Accessories';
    const eyebrow = f.model ? `MADE TO FIT YOUR ${f.model.toUpperCase()}` : f.brand ? `MADE TO FIT YOUR ${f.brand.toUpperCase()}` : collectionTitle ? (/best-sellers/.test(shopPath) ? 'OUR MOST POPULAR ACCESSORIES' : 'CURATED COLLECTION') : 'FOR HONDA & ROYAL ENFIELD';
    const scope = f.model || f.brand || 'all';
    const count = n => `${n} ${n === 1 ? 'accessory' : 'accessories'}`;
    const bikeChips = f.brand
      ? [`<a class="shop-chip" href="${esc(shopURL({ brand: '', model: '', category: f.category, q: '' }, f))}">All bikes</a>`,
        `<a class="shop-chip" href="${esc(shopURL({ brand: f.brand, model: '' }, f))}" ${!f.model ? 'aria-current="true"' : ''}>All ${esc(f.brand)}</a>`,
        ...bikes[f.brand].map(model => {
          const photo = bikePhoto(f.brand, model);
          return `<a class="shop-chip shop-chip-bike" href="${esc(shopURL({ brand: f.brand, model }, f))}" data-bike-brand="${esc(f.brand)}" data-bike-model="${esc(model)}" ${f.model === model ? 'aria-current="true"' : ''}>${photo ? `<img src="${esc(photo.src)}" width="${photo.width}" height="${photo.height}" alt="" loading="lazy" decoding="async">` : ''}<span>${esc(model)}</span></a>`;
        })]
      : [`<a class="shop-chip" href="${esc(shopURL({ brand: '', model: '' }, f))}" aria-current="true">All bikes</a>`,
        ...Object.keys(bikes).map(brand => `<a class="shop-chip" href="${esc(shopURL({ brand, model: '' }, f))}">${esc(brand)}</a>`)];
    const typeChips = ['', ...categories].map((category, i) => {
      const n = shopProducts({ ...f, category, q: '' }, pool).length;
      return `<button type="button" class="shop-chip" id="category-${category ? i - 1 : 'all'}" data-action="part-category" data-category="${esc(category)}" aria-pressed="${f.category === category}">${esc(category || 'All types')}<span class="shop-chip-count">${n}</span></button>`;
    }).join('');
    return `<nav class="breadcrumb" aria-label="Breadcrumb"><a href="${esc(rootURL())}">Home</a>${icon('chevron')}${collectionTitle ? `<a href="${esc(routes.all)}">All accessories</a>${icon('chevron')}` : ''}${f.brand ? `<a href="${esc(bikeSelectionURL(f.brand))}">${esc(f.brand)}</a>${icon('chevron')}` : ''}<a href="${esc(shopURL({ category: '', q: '' }, f))}">${esc(f.model || collectionTitle || 'All accessories')}</a>${f.category ? `${icon('chevron')}<span aria-current="page">${esc(f.category)}</span>` : ''}</nav>
      <header class="shop-head"><div class="shop-head-copy"><p class="eyebrow">${esc(eyebrow)}</p><h1>${esc(title)}</h1></div><div class="shop-meta"><p class="shop-count" aria-live="polite">${count(list.length)}</p><label class="sr-only" for="sort">Sort by</label><select id="sort" class="shop-sort" data-action="sort">${[['featured', 'Recommended'], ['price-low', 'Price: low to high'], ['price-high', 'Price: high to low'], ['name', 'Name']].map(([value, label]) => `<option value="${value}" ${f.sort === value ? 'selected' : ''}>${label}</option>`).join('')}</select></div></header>
      <div class="chip-scroller"><button type="button" class="chip-scroll chip-scroll-prev" data-action="chip-scroll" data-dir="-1" aria-label="Show previous bikes" tabindex="-1">${icon('arrow-left')}</button><div class="shop-chip-row shop-bike-row" data-seg="shop-bike" role="group" aria-label="Bike">${bikeChips.join('')}</div><button type="button" class="chip-scroll chip-scroll-next" data-action="chip-scroll" data-dir="1" aria-label="Show more bikes" tabindex="-1">${icon('arrow')}</button></div>
      <div class="shop-type-anchor" aria-hidden="true"></div>
      <div class="shop-chip-row shop-type-bar" data-seg="shop-type" role="group" aria-label="Accessory type">${typeChips}</div>
      <form class="shop-search" data-form="collection-search" role="search"><label class="sr-only" for="collection-query">Search ${esc(scope)} accessories</label>${icon('search')}<input type="search" id="collection-query" name="q" value="${esc(f.q)}" placeholder="Search accessories" autocomplete="off"><button type="submit" class="sr-only">Search</button></form>
      ${f.q ? `<div class="active-filters"><button class="filter-chip" data-action="remove-filter" data-key="q" aria-label="Remove search ${esc(f.q)}"><span>Search: “${esc(f.q)}”</span>${icon('close')}</button></div>` : ''}
      <section class="collection-results" aria-labelledby="results-title"${f.model ? ' data-model' : ''}><h2 class="sr-only" id="results-title" tabindex="-1">${f.q ? `Search results for ${esc(f.q)}` : esc(f.category || title)}</h2>
      ${list.length ? `<div class="od-grid collection-products">${list.map(p => productCard(p, 'quick')).join('')}</div>` : `<div class="empty-state"><h2>${emptyBrand ? `No ${esc(f.brand)} accessories listed yet.` : 'No listings for this selection.'}</h2><p>${emptyBrand ? 'Browse the current catalogue to explore available accessories.' : 'Try another type or clear your search.'}</p><div class="empty-actions od-cluster">${emptyBrand ? `<a class="btn btn-dark" href="${esc(shopURL({ brand: '', model: '', category: '', q: '' }))}">Browse all accessories ${icon('arrow')}</a>` : `<button class="btn btn-dark" data-action="clear-filters">Clear filters</button>`}</div></div>`}
      </section>`;
  }
  // Long bike rows stay on one line; edge fades and arrow buttons show when there is more to scroll to.
  function syncChipScroller(scroller) {
    const row = scroller.querySelector('.shop-chip-row');
    if (!row) return;
    const max = row.scrollWidth - row.clientWidth;
    scroller.classList.toggle('can-prev', row.scrollLeft > 4);
    scroller.classList.toggle('can-next', row.scrollLeft < max - 4);
  }
  function renderShop(silent = false) {
    if (!shopRoot) return;
    const f = getFilters();
    rememberShop(currentPath());
    redraw(shopRoot, shop());
    shopRoot.dataset.rendered = 'true';
    syncSegments(shopRoot);
    if (!firstRender && !silent) shopRoot.querySelectorAll('.collection-products').forEach(animateIn);
    shopRoot.querySelectorAll('.shop-chip-row').forEach(row => {
      const active = row.querySelector('[aria-current="true"],[aria-pressed="true"]');
      if (active && row.scrollWidth > row.clientWidth) row.scrollLeft = Math.max(0, active.offsetLeft - row.offsetLeft - (row.clientWidth - active.offsetWidth) / 2);
    });
    shopRoot.querySelectorAll('.chip-scroller').forEach(syncChipScroller);
    const scopeName = [f.brand, f.model].filter(Boolean).join(' ');
    document.title = scopeName ? `${scopeName} Parts & Accessories | ${shopName}` : seoTitle;
  }

  /* Product page */
  function bikeName(selected = bike) {
    return [selected.brand, selected.model].filter(Boolean).join(' · ');
  }
  function cleanBikeText(value) {
    return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').slice(0, 120) : '';
  }
  function normalizeProductBike(value) {
    if (!value || typeof value !== 'object' || !['none', 'listed', 'other'].includes(value.mode)) return null;
    const empty = { mode: 'none', brand: '', model: '', otherName: '' };
    if (value.mode === 'none') return empty;
    if (value.mode === 'other') return { ...empty, mode: 'other', otherName: cleanBikeText(value.otherName) };
    const brand = cleanBikeText(value.brand), model = cleanBikeText(value.model);
    return brand && model ? { ...empty, mode: 'listed', brand, model } : null;
  }
  function productBikeName(selected) {
    if (!selected) return 'Bike not recorded';
    if (selected.mode === 'none') return 'No bike selected';
    return selected.mode === 'listed' ? bikeName(selected) : selected.otherName || 'Other bike';
  }
  const listJoin = items => items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0] || '';
  function shopperFitment(p) {
    const made = listJoin(p.models);
    const brandModels = made ? `${p.brand} ${made}` : '';
    if (p.brand === 'Universal') {
      return { selected: normalizeProductBike(bike.model ? { mode: 'other', otherName: bikeName() } : { mode: 'none' }), fit: { kind: 'unknown', title: 'Universal fit', text: 'Fits most bikes. Check mounts and size.' }, headline: 'Fits most bikes', detail: 'Check the mounting points on your bike before ordering.' };
    }
    const yourBike = `${bike.brand} ${bike.model}`;
    if (bike.model && bike.brand === p.brand && p.models.includes(bike.model)) {
      return { selected: normalizeProductBike({ mode: 'listed', brand: bike.brand, model: bike.model }), fit: { kind: 'listed', title: `Fits ${bike.model}`, text: 'Made for this bike.' }, headline: `Fits your ${yourBike}`, detail: 'Made for this bike.' };
    }
    if (bike.model) {
      return { selected: normalizeProductBike({ mode: 'other', otherName: bikeName() }), fit: { kind: 'mismatch', title: `Doesn't fit ${bike.model}`, text: brandModels ? `Made for ${brandModels}.` : 'Ask us to confirm the fit.' }, headline: `Doesn’t fit your ${yourBike}`, detail: made ? 'Made for the bikes above.' : 'Ask us to confirm the fit.' };
    }
    return { selected: normalizeProductBike({ mode: 'none' }), fit: { kind: 'unknown', title: brandModels ? `Made for ${brandModels}` : 'Fitment to confirm', text: 'No bike was chosen.' }, headline: made ? 'Which bike do you ride?' : 'Fitment to confirm', detail: made ? 'Tap your bike above to check the fit.' : 'Ask us to confirm the fit.' };
  }
  const productData = onProductPage ? readJSON('mg-product-json') : null;
  const productPage = productData ? {
    ...(findProduct(productData.id) || {}),
    ...productData,
    brand: brandNames.includes(productData.vendor) ? productData.vendor : 'Universal',
    category: categoryOf(productData.category),
    models: Array.isArray(productData.models) ? productData.models : []
  } : null;
  const productState = productPage ? { variant: String(productPage.selectedVariant), qty: 1, image: 0 } : null;
  function fitmentPanel(p) {
    const { fit, headline } = shopperFitment(p);
    const universal = p.brand === 'Universal';
    const change = '<button type="button" class="pdp-fit-help" data-action="choose-bike">Change</button>';
    let body;
    if (universal) {
      body = `<p class="pdp-fit-row">${icon('info')}<strong id="product-fitment-status">Fits most bikes · check mounts</strong><button type="button" class="pdp-fit-help" data-action="help">Fitment help</button></p>`;
    } else if (fit.kind === 'listed' || fit.kind === 'mismatch') {
      body = `<p class="pdp-fit-row">${icon(fit.kind === 'listed' ? 'check' : 'close')}<strong id="product-fitment-status" role="status" aria-live="polite">${esc(headline)}</strong>${change}</p>`
        + (fit.kind === 'mismatch' && p.models.length ? `<p class="pdp-fit-note">Made for ${esc(listJoin(p.models))}</p>` : '');
    } else if (p.models.length) {
      body = `<div class="pdp-fit-row pdp-fit-pick"><span class="pdp-fit-label" id="fit-label">Your bike:</span><div class="pdp-fit-pills" role="group" aria-labelledby="fit-label">${p.models.map(model => {
          const photo = bikePhoto(p.brand, model);
          return `<button type="button" class="shop-chip shop-chip-bike pdp-fit-pill" data-action="fit-pick" data-brand="${esc(p.brand)}" data-model="${esc(model)}" aria-label="${esc(`${p.brand} ${model}`)}, set as my bike">${photo ? `<img src="${esc(photo.src)}" width="${photo.width}" height="${photo.height}" alt="" loading="lazy" decoding="async">` : ''}<span>${esc(model)}</span></button>`;
        }).join('')}<button type="button" class="pdp-fit-help pdp-fit-other-link" data-action="choose-bike">Other</button></div></div><p class="sr-only" id="product-fitment-status" role="status" aria-live="polite">Tap your bike to check the fit.</p>`;
    } else {
      body = `<p class="pdp-fit-row">${icon('info')}<strong id="product-fitment-status">Ask us to confirm the fit</strong><button type="button" class="pdp-fit-help" data-action="help">Fitment help</button></p>`;
    }
    return `<section class="pdp-fit pdp-fit-compact" aria-label="Fitment" data-fitment-panel data-fit="${fit.kind}">${body}</section>`;
  }
  // Part kinds that pair well on one bike: protection pieces together, luggage pieces together, cockpit and comfort pieces together.
  const partKinds = [
    ['crash guard', 'frame slider', 'sump guard', 'headlight grill', 'tyre hugger', 'master cylinder guard', 'caliper guard', 'radiator grill', 'oil cap', 'tail tidy'],
    ['saddle stay', 'carrier', 'backrest', 'top box', 'jerry can'],
    ['visor', 'gps mount', 'handle bar riser', 'handlebar riser', 'footrest', 'seat'],
    ['fog light clamp', 'fog light mount', 'foglamb mount', 'fog light', 'foglight'],
  ];
  function partKind(p) {
    const name = `${p.shortName} ${p.title}`.toLowerCase().replace(/sum guard/g, 'sump guard').replace(/back rest/g, 'backrest').replace(/foot rest/g, 'footrest').replace(/wind ?(shield|screen)/g, 'visor');
    for (const group of partKinds) for (const kind of group) if (name.includes(kind)) return { group, kind };
    return null;
  }
  function relatedProducts(p) {
    const selected = bike.model ? bike : null;
    const relatedBrand = selected ? selected.brand : p.brand;
    const relatedModels = selected ? [selected.model] : p.models;
    const forBike = other => other.id !== p.id && other.brand === relatedBrand && (relatedBrand === 'Universal' ? other.category === p.category : !relatedModels.length || other.models.some(model => relatedModels.includes(model)));
    const own = partKind(p);
    const pairs = [];
    if (own && p.brand !== 'Universal') {
      // Pairs are always for the bike this part is made for (the saved bike when it is one of them).
      const pairModels = bike.brand === p.brand && p.models.includes(bike.model) ? [bike.model] : p.models;
      const pairsWith = other => other.id !== p.id && other.brand === p.brand && other.models.some(model => pairModels.includes(model));
      const seen = new Set([own.kind]);
      const candidates = products.filter(other => pairsWith(other) || (own.group === partKinds[3] && other.brand === 'Universal' && other.category === 'Lighting'))
        .map(other => ({ other, kind: partKind(other) }))
        .filter(({ other, kind }) => kind && hasStock(other))
        .sort((a, b) => Number(b.kind.group === own.group) - Number(a.kind.group === own.group) || Number(b.other.price < p.price) - Number(a.other.price < p.price));
      for (const { other, kind } of candidates) {
        if (pairs.length === 4) break;
        if (seen.has(kind.kind)) continue;
        seen.add(kind.kind); pairs.push(other);
      }
    }
    const paired = new Set(pairs.map(item => item.id));
    const related = products.filter(other => forBike(other) && !paired.has(other.id))
      .sort((a, b) => Number(b.category === p.category) - Number(a.category === p.category) || Number(hasStock(b)) - Number(hasStock(a))).slice(0, 4);
    const bikeLabel = selected?.model || p.models.join(' / ') || relatedBrand;
    const pairBike = bike.brand === p.brand && p.models.includes(bike.model) ? bike.model : p.models.join(' / ');
    const pairSection = pairs.length > 1 ? `<section class="section related-section pairs-section"><div class="container"><div class="section-head"><div><p class="eyebrow">Complete the setup</p><h2>Goes well with</h2><p>Accessories that pair with the ${esc(p.shortName)} on ${p.models.length > 1 && pairBike.includes(' / ') ? 'these bikes' : `the ${esc(pairBike)}`}.</p></div></div><div class="od-grid home-products">${pairs.map(item => productCard(item, 'quick')).join('')}</div></div></section>` : '';
    if (!related.length) return pairSection;
    return `${pairSection}<section class="section related-section"><div class="container"><div class="section-head"><div><h2>${relatedBrand === 'Universal' ? 'More universal options' : 'More for your ride'}</h2><p>${relatedBrand === 'Universal' ? 'Other accessories that fit most bikes.' : `More accessories for ${esc(bikeLabel)}.`}</p></div></div><div class="od-grid home-products">${related.map(item => productCard(item, 'quick')).join('')}</div></div></section>`;
  }
  function renderProductDynamic() {
    const p = productPage;
    if (!p) return;
    const panel = document.querySelector('[data-fitment-panel]');
    if (panel) panel.outerHTML = fitmentPanel(p);
    const related = document.querySelector('[data-related-root]');
    if (related) redraw(related, relatedProducts(p));
    const whatsapp = document.querySelector('[data-whatsapp]');
    if (whatsapp) {
      const fits = bike.model && bike.brand === p.brand ? `my ${bike.brand} ${bike.model}` : 'my bike';
      whatsapp.href = `https://wa.me/${whatsapp.dataset.whatsapp}?text=${encodeURIComponent(`Hi, will the ${p.title} fit ${fits}? ${location.origin}${p.url}`)}`;
    }
    const shopLink = document.querySelector('[data-product-shop-link]');
    if (shopLink && validShop(storedShop)) {
      shopLink.href = lastShop;
      shopLink.textContent = `${getFilters(queryOf(lastShop)).brand || 'All'} accessories`;
    }
    updateHeader();
  }
  function rememberPurchase() {
    const form = document.querySelector('[data-form="purchase"]');
    if (!form || !productState) return;
    const qty = Number(form.elements.quantity.value);
    if (Number.isInteger(qty) && qty >= 1 && qty <= 99) productState.qty = qty;
    const variant = form.elements.namedItem('id')?.value;
    if (productPage.variants.some(v => String(v.id) === variant)) productState.variant = variant;
  }
  const priceMarkup = v => `<span class="price od-nowrap">${money(v.price)}</span>${v.compareAt > v.price ? `<s class="compare-price od-nowrap">${money(v.compareAt)}</s>` : ''}${offBadge(v.price, v.compareAt)}`;
  function syncPurchaseControls() {
    const form = document.querySelector('[data-form="purchase"]');
    const p = productPage;
    if (!form || !p) return;
    rememberPurchase();
    const v = p.variants.find(item => String(item.id) === form.elements.namedItem('id')?.value);
    if (!v) return;
    const qty = Number(form.elements.quantity.value);
    const valid = Number.isInteger(qty) && qty >= 1 && qty <= 99;
    document.getElementById('product-price').innerHTML = priceMarkup(v);
    const save = document.getElementById('product-save');
    if (save) { save.hidden = !(v.compareAt > v.price); save.textContent = v.compareAt > v.price ? `You save ${money(v.compareAt - v.price)}` : ''; }
    const stock = document.getElementById('product-availability'); stock.textContent = v.available ? 'In stock' : 'Out of stock'; stock.dataset.available = String(v.available);
    const sku = document.getElementById('product-sku'); sku.textContent = v.sku ? `SKU: ${v.sku}` : ''; sku.hidden = !v.sku;
    const add = document.getElementById('add-to-cart');
    add.disabled = !v.available;
    add.innerHTML = `${icon(v.available ? 'bag-plus' : 'bag')}${v.available ? 'Add to cart' : 'Out of stock'}`;
    const sticky = document.getElementById('mobile-add-to-cart'); sticky.disabled = !v.available; sticky.innerHTML = v.available ? `${icon('bag-plus')}Add to cart` : 'Out of stock';
    document.getElementById('mobile-purchase-price').textContent = money(v.price * (valid ? qty : 1));
    document.getElementById('mobile-purchase-quantity').textContent = valid ? `Total for ${qty} ${qty === 1 ? 'item' : 'items'}` : 'Check quantity';
    form.querySelector('[data-action="quantity-minus"]').disabled = valid && qty <= 1;
    form.querySelector('[data-action="quantity-plus"]').disabled = valid && qty >= 99;
  }
  function syncPurchaseBar() {
    const bar = document.getElementById('mobile-purchase');
    const action = document.getElementById('add-to-cart');
    if (!bar || !action) { document.documentElement.style.setProperty('--purchase-space', '0px'); return; }
    const rect = action.getBoundingClientRect();
    const viewport = window.visualViewport;
    const visibleBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    const visibleTop = siteHeader ? siteHeader.getBoundingClientRect().bottom : 0;
    const fieldActive = document.activeElement?.matches('input,select,textarea');
    const show = matchMedia('(max-width:767px)').matches && !document.querySelector('dialog[open]') && !fieldActive && (rect.bottom <= visibleTop || rect.top >= visibleBottom);
    bar.hidden = !show;
    document.documentElement.style.setProperty('--purchase-space', show ? `${bar.getBoundingClientRect().height}px` : '0px');
  }
  function setupPurchaseBar() {
    purchaseResizeObserver?.disconnect();
    const bar = document.getElementById('mobile-purchase');
    if (bar && 'ResizeObserver' in window) { purchaseResizeObserver = new ResizeObserver(syncPurchaseBar); purchaseResizeObserver.observe(bar); }
    syncPurchaseControls(); syncPurchaseBar();
  }
  const galleryZoom = { slide: null, scale: 1, x: 0, y: 0 };
  function hideGalleryHint() { document.getElementById('gallery-hint')?.classList.add('is-hidden'); }
  function applyGalleryZoom(slide, scale, x, y, animate) {
    const img = slide?.querySelector('img');
    if (!img) return;
    const maxX = (scale - 1) * slide.clientWidth / 2;
    const maxY = (scale - 1) * slide.clientHeight / 2;
    x = Math.max(-maxX, Math.min(maxX, x));
    y = Math.max(-maxY, Math.min(maxY, y));
    Object.assign(galleryZoom, { slide, scale, x, y });
    img.style.transformOrigin = '';
    img.style.transition = animate ? 'transform 220ms ease-out' : 'none';
    img.style.transform = scale > 1 ? `translate(${x}px, ${y}px) scale(${scale})` : '';
    slide.classList.toggle('is-zoomed', scale > 1);
    document.getElementById('gallery-slides')?.classList.toggle('is-zoomed', scale > 1);
    if (scale > 1) hideGalleryHint();
  }
  function resetGalleryZoom(animate = true) {
    if (galleryZoom.slide && galleryZoom.scale !== 1) applyGalleryZoom(galleryZoom.slide, 1, 0, 0, animate);
  }
  function zoomGalleryAt(slide, clientX, clientY, scale) {
    const r = slide.getBoundingClientRect();
    const px = clientX - (r.left + r.width / 2), py = clientY - (r.top + r.height / 2);
    applyGalleryZoom(slide, scale, px * (1 - scale), py * (1 - scale), true);
  }
  function selectGallery(p, index, scroll = true) {
    if (!p.images[index]) return;
    if (index !== productState.image) resetGalleryZoom(false);
    productState.image = index;
    const slides = document.getElementById('gallery-slides');
    if (scroll && slides) slides.scrollTo({ left: slides.clientWidth * index, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    const counter = document.getElementById('gallery-counter');
    if (counter) counter.textContent = `${index + 1} / ${p.images.length}`;
    const prev = document.querySelector('.pdp-arrow-prev'), next = document.querySelector('.pdp-arrow-next');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === p.images.length - 1;
    document.querySelectorAll('[data-action="gallery"]').forEach(button => {
      const active = Number(button.dataset.index) === index;
      button.setAttribute('aria-pressed', String(active));
      if (active) button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });
  }
  function setupGallery() {
    const slides = document.getElementById('gallery-slides');
    if (!slides || !productPage) return;
    setTimeout(hideGalleryHint, 6000);
    let frame = null;
    slides.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const index = Math.round(slides.scrollLeft / Math.max(1, slides.clientWidth));
        if (index !== productState.image) selectGallery(productPage, index, false);
      });
    }, { passive: true });
    slides.addEventListener('keydown', e => {
      const slide = slides.children[productState.image];
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        selectGallery(productPage, Math.max(0, Math.min(productPage.images.length - 1, productState.image + (e.key === 'ArrowRight' ? 1 : -1))));
      } else if ((e.key === 'Enter' || e.key === ' ') && slide) {
        e.preventDefault();
        if (galleryZoom.slide === slide && galleryZoom.scale > 1) resetGalleryZoom();
        else applyGalleryZoom(slide, 2, 0, 0, true);
      } else if (e.key === 'Escape') resetGalleryZoom();
    });
    const touch = { mode: null, startX: 0, startY: 0, startTime: 0, lastTap: 0, lastX: 0, lastY: 0, d0: 0, s0: 1, x0: 0, y0: 0, mx: 0, my: 0, slide: null };
    const distance = t => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    slides.addEventListener('touchstart', e => {
      const slide = e.target.closest('.pdp-slide');
      if (!slide) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        const r = slide.getBoundingClientRect();
        const zoomed = galleryZoom.slide === slide;
        Object.assign(touch, { mode: 'pinch', slide, d0: distance(e.touches), s0: zoomed ? galleryZoom.scale : 1, x0: zoomed ? galleryZoom.x : 0, y0: zoomed ? galleryZoom.y : 0,
          mx: (e.touches[0].clientX + e.touches[1].clientX) / 2 - (r.left + r.width / 2), my: (e.touches[0].clientY + e.touches[1].clientY) / 2 - (r.top + r.height / 2) });
        hideGalleryHint();
      } else if (e.touches.length === 1) {
        const t = e.touches[0];
        Object.assign(touch, { startX: t.clientX, startY: t.clientY, startTime: Date.now(), slide });
        if (galleryZoom.slide === slide && galleryZoom.scale > 1) {
          e.preventDefault();
          Object.assign(touch, { mode: 'pan', x0: galleryZoom.x, y0: galleryZoom.y });
        } else touch.mode = 'tap';
      }
    }, { passive: false });
    slides.addEventListener('touchmove', e => {
      if (touch.mode === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const scale = Math.max(1, Math.min(4, touch.s0 * distance(e.touches) / touch.d0));
        const ratio = scale / touch.s0;
        applyGalleryZoom(touch.slide, scale, touch.mx - (touch.mx - touch.x0) * ratio, touch.my - (touch.my - touch.y0) * ratio, false);
      } else if (touch.mode === 'pan' && e.touches.length === 1) {
        e.preventDefault();
        const t = e.touches[0];
        applyGalleryZoom(touch.slide, galleryZoom.scale, touch.x0 + t.clientX - touch.startX, touch.y0 + t.clientY - touch.startY, false);
      } else if (touch.mode === 'tap' && e.touches.length === 1) {
        const t = e.touches[0];
        if (Math.hypot(t.clientX - touch.startX, t.clientY - touch.startY) > 10) touch.mode = null;
      }
    }, { passive: false });
    slides.addEventListener('touchend', e => {
      if (touch.mode === 'pinch') {
        if (e.touches.length === 0) { if (galleryZoom.scale < 1.1) resetGalleryZoom(); touch.mode = null; }
        return;
      }
      if (touch.mode === 'pan' || touch.mode === 'tap') {
        const quick = Date.now() - touch.startTime < 300;
        const moved = Math.hypot(e.changedTouches[0].clientX - touch.startX, e.changedTouches[0].clientY - touch.startY) > 10;
        if (quick && !moved) {
          const now = Date.now();
          if (now - touch.lastTap < 320 && Math.hypot(touch.startX - touch.lastX, touch.startY - touch.lastY) < 30) {
            e.preventDefault();
            if (galleryZoom.slide === touch.slide && galleryZoom.scale > 1) resetGalleryZoom();
            else zoomGalleryAt(touch.slide, touch.startX, touch.startY, 2.5);
            touch.lastTap = 0;
          } else Object.assign(touch, { lastTap: now, lastX: touch.startX, lastY: touch.startY });
        }
      }
      touch.mode = null;
    });
    // With a mouse, clicking a photo opens it full screen; touch keeps pinch and double-tap zoom.
    if (matchMedia('(hover: hover) and (pointer: fine)').matches) slides.querySelectorAll('.pdp-slide').forEach(slide => { slide.dataset.action = 'zoom'; });
    window.addEventListener('resize', () => resetGalleryZoom(false), { passive: true });
  }
  function paintLightbox() {
    if (!lightboxState || !productPage) return;
    const p = productPage; const im = p.images[lightboxState.index];
    if (!im) return;
    const stage = document.getElementById('lightbox-stage');
    stage.classList.toggle('is-zoomed', lightboxState.zoomed);
    stage.classList.remove('is-hover-zoom');
    stage.innerHTML = `<div class="lightbox-picture">${imageTag(im, `${p.title}, image ${lightboxState.index + 1}`, 'fetchpriority="high"')}</div>`;
    stage.scrollTo({ top: 0, left: 0 });
    document.getElementById('lightbox-caption').textContent = `${p.shortName} — image ${lightboxState.index + 1} of ${p.images.length}`;
    const zoom = document.getElementById('lightbox-zoom'); zoom.textContent = lightboxState.zoomed ? 'Fit image' : 'Zoom in'; zoom.setAttribute('aria-pressed', String(lightboxState.zoomed));
    document.getElementById('lightbox-previous').disabled = lightboxState.index === 0;
    document.getElementById('lightbox-next').disabled = lightboxState.index === p.images.length - 1;
  }
  // With a mouse, the viewer magnifies the spot under the pointer, unless "Zoom in" has already enlarged the photo.
  (() => {
    const stage = document.getElementById('lightbox-stage');
    if (!stage || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    let box = null;
    const reset = () => {
      const picture = stage.querySelector('.lightbox-picture');
      if (picture) { picture.style.transform = ''; picture.style.transformOrigin = ''; }
      stage.classList.remove('is-hover-zoom'); box = null;
    };
    stage.addEventListener('mousemove', e => {
      const picture = stage.querySelector('.lightbox-picture');
      if (!picture || lightboxState?.zoomed) return;
      if (!stage.classList.contains('is-hover-zoom')) box = picture.getBoundingClientRect(); // measured before scaling
      const x = Math.min(100, Math.max(0, (e.clientX - box.left) / box.width * 100));
      const y = Math.min(100, Math.max(0, (e.clientY - box.top) / box.height * 100));
      picture.style.transformOrigin = `${x}% ${y}%`;
      picture.style.transform = 'scale(2.5)';
      stage.classList.add('is-hover-zoom');
    });
    stage.addEventListener('mouseleave', reset);
    window.addEventListener('resize', reset, { passive: true });
  })();
  function moveLightbox(delta) {
    if (!lightboxState || !productPage) return;
    const p = productPage;
    lightboxState.index = Math.max(0, Math.min(p.images.length - 1, lightboxState.index + delta)); lightboxState.zoomed = false;
    selectGallery(p, lightboxState.index); paintLightbox();
  }

  /* Cart */
  const cartURL = path => `${path}.js`;
  async function refreshCart() {
    const response = await fetch(cartURL(routes.cart), { headers: { Accept: 'application/json' } });
    cart = await response.json();
    updateHeader();
    return cart;
  }
  async function changeCartLine(key, quantity) {
    const response = await fetch(cartURL(routes.cartChange), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
    if (!response.ok) { const error = await response.json().catch(() => ({})); notify(error.description || 'Your cart could not be updated.'); return refreshCart(); }
    cart = await response.json();
    cartChanged = true;
    updateHeader();
    return cart;
  }
  function cartFitBadge(item) {
    const props = item.properties || {};
    const kind = props.Bike ? props._fit_kind || 'unknown' : 'unknown';
    const title = props.Bike ? props._fit_title || 'Check fitment' : 'Check fitment';
    const label = kind === 'unknown' && title !== 'Universal fit' ? 'Check fitment' : title;
    return `<span class="cart-badge cart-badge-fit" data-fit="${esc(kind)}" title="${esc(props._fit_text || '')}">${kind === 'listed' ? icon('check') : kind === 'mismatch' ? icon('close') : ''}${esc(label)}</span>`;
  }
  function cartCompareAt(item) {
    const p = findProduct(item.handle);
    return p && String(p.variantId) === String(item.variant_id) && p.compareAt > item.final_price / 100 ? p.compareAt : 0;
  }
  function cartLine(item) {
    const p = findProduct(item.handle);
    const name = p?.shortName || item.product_title;
    const key = item.key;
    const image = p?.image || (item.image ? { src: item.image, width: 600, height: 600 } : null);
    const compareAt = cartCompareAt(item);
    const unit = item.final_price / 100;
    const off = compareAt ? percentOff(unit, compareAt) : 0;
    return `<article class="cart-line" data-cart-key="${esc(key)}">
      <a class="cart-line-img" href="${esc(item.url)}" tabindex="-1" aria-hidden="true">${imageTag(image, '')}</a>
      <div class="cart-line-main">
        <div class="cart-line-top"><a class="cart-line-name" href="${esc(item.url)}">${esc(name)}</a><button type="button" class="cart-line-remove" data-action="remove-item" data-key="${esc(key)}" aria-label="Remove ${esc(name)}">${icon('trash')}</button></div>
        <div class="cart-line-badges">${cartFitBadge(item)}${item.product_has_only_default_variant ? '' : `<span class="cart-badge">${esc(item.variant_title)}</span>`}${off >= 1 ? `<span class="cart-badge cart-badge-off">${off}% OFF</span>` : ''}</div>
        <div class="cart-line-bottom">
          <div class="quantity-control cart-qty"><button type="button" data-action="cart-minus" data-key="${esc(key)}" aria-label="Decrease quantity of ${esc(name)}" ${item.quantity <= 1 ? 'disabled' : ''}>${icon('minus')}</button><input type="number" inputmode="numeric" min="1" max="99" value="${item.quantity}" aria-label="Quantity of ${esc(name)}" data-action="cart-quantity" data-key="${esc(key)}"><button type="button" data-action="cart-plus" data-key="${esc(key)}" aria-label="Increase quantity of ${esc(name)}" ${item.quantity >= 99 ? 'disabled' : ''}>${icon('plus')}</button></div>
          <div class="cart-line-price"><span><strong>${money(item.final_line_price / 100)}</strong>${compareAt ? `<s>${money(compareAt * item.quantity)}</s>` : ''}</span>${item.quantity > 1 ? `<small>${money(unit)} each</small>` : ''}</div>
        </div>
      </div>
    </article>`;
  }
  function cartTotals() {
    const count = cart.item_count || 0;
    const subtotal = (cart.total_price || 0) / 100;
    const savings = (cart.items || []).reduce((sum, item) => { const c = cartCompareAt(item); return sum + (c ? (c - item.final_price / 100) * item.quantity : 0); }, 0);
    const mismatches = (cart.items || []).filter(item => item.properties?._fit_kind === 'mismatch').length;
    return { count, subtotal, savings, mismatches };
  }
  function cartWarning() {
    const { mismatches } = cartTotals();
    return mismatches ? `<p class="cart-warning">${icon('info')}<span>${mismatches === 1 ? '1 accessory doesn’t' : `${mismatches} accessories don’t`} fit the bike chosen for ${mismatches === 1 ? 'it' : 'them'}. Check before checkout.</span></p>` : '';
  }
  function cartSummary() {
    const { count, subtotal, savings } = cartTotals();
    return `<div class="cart-summary">
      <div class="cart-sum-row"><span>Subtotal (${count} ${count === 1 ? 'item' : 'items'})</span><strong>${money(subtotal)}</strong></div>
      ${savings > 0 ? `<div class="cart-sum-row cart-sum-save"><span>You save</span><span>${money(savings)}</span></div>` : ''}
      <p class="cart-sum-note">${config.taxesIncluded ? 'Tax included.' : 'Taxes calculated at checkout.'} Shipping calculated at checkout.</p>
      <a class="btn cart-checkout" href="/checkout">${icon('lock')}Checkout · ${money(subtotal)}</a>
      <p class="cart-secure">${icon('shield')}Secure checkout</p>
    </div>`;
  }
  function cartEmpty(level = 'h3') {
    const label = bike.model ? `Shop accessories for your ${bike.model}` : 'Browse accessories';
    return `<div class="cart-empty">${icon('bag')}<${level}>Your cart is empty</${level}><p>Accessories for your bike are a tap away.</p><a class="btn btn-dark" href="${esc(shopURL())}">${esc(label)} ${icon('arrow')}</a></div>`;
  }
  function renderCart() {
    const dialog = document.getElementById('cart-dialog');
    const count = cart.item_count || 0;
    document.querySelectorAll('[data-cart-title-count]').forEach(el => { el.textContent = count ? `· ${count}` : ''; });
    if (dialog) {
      const bodyEl = dialog.querySelector('.dialog-body');
      const footer = dialog.querySelector('.dialog-footer');
      if (!cart.items?.length) { bodyEl.innerHTML = cartEmpty(); footer.hidden = true; }
      else { footer.hidden = false; bodyEl.innerHTML = cartWarning() + cart.items.map(cartLine).join(''); footer.innerHTML = cartSummary(); }
    }
    const page = document.querySelector('[data-cart-page]');
    if (page) {
      page.innerHTML = cart.items?.length
        ? `<header class="shop-head"><div class="shop-head-copy"><h1>Your cart</h1></div><div class="shop-meta"><p class="shop-count">${count} ${count === 1 ? 'item' : 'items'}</p></div></header>
           <div class="cart-page-layout"><div class="cart-page-lines">${cartWarning()}${cart.items.map(cartLine).join('')}<a class="text-link cart-continue" href="${esc(shopURL())}">${icon('arrow-left')} Continue shopping</a></div><aside class="cart-page-summary" aria-label="Order summary">${cartSummary()}</aside></div>`
        : `<div class="cart-page-empty">${cartEmpty('h1')}</div>`;
    }
  }
  const cartItem = key => cart.items?.find(item => item.key === key);
  const focusSelector = selector => document.querySelector(selector)?.focus();

  /* Search dialog */
  function setSearchScope(context) {
    searchContext = { ...context };
    document.getElementById('search-title').textContent = `Search ${context.model || context.brand || 'all'} accessories`;
    document.getElementById('search-scope').textContent = [context.brand || 'All brands', context.model, context.category || 'All types'].filter(Boolean).join(' · ');
    document.getElementById('search-all-brands').hidden = !context.brand && !context.model && !context.category;
    searchSuggestions(document.getElementById('search-input').value);
  }
  const popularSearches = ['Crash guard', 'Visor', 'Fog light', 'Carrier', 'Seat', 'Sump guard'];
  const markTerms = (text, query) => {
    const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean).map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!terms.length) return esc(text);
    return esc(text).replace(new RegExp(`(${terms.map(esc).join('|')})`, 'gi'), '<mark>$1</mark>');
  };
  function searchSuggestions(query) {
    const target = document.getElementById('search-suggestions');
    const q = query.trim();
    const context = { ...(searchContext || browseContext()), q, sort: 'featured' };
    const list = matchingProducts(context).slice(0, 6);
    const universal = context.brand ? universalProducts(context).slice(0, 2) : [];
    const clear = document.querySelector('#search-dialog .search-clear');
    if (clear) clear.hidden = !query;
    const row = p => { const im = imageFor(p); return `<a class="search-result" href="${esc(p.url)}"><span class="search-result-thumb">${im ? `<img src="${esc(im.src)}" alt="" loading="lazy" decoding="async">` : ''}</span><span class="search-result-copy"><strong>${markTerms(p.shortName, q)}</strong><span>${esc(fitLabel(p))}</span></span><span class="search-result-price">${money(p.price)}</span></a>`; };
    const bikeRows = q ? bikeMatches(q).slice(0, 4).map(({ brand, model }) => {
      const photo = bikePhoto(brand, model);
      const n = products.filter(p => p.brand === brand && p.models.includes(model)).length;
      return `<a class="search-result search-bike" href="${esc(shopURL({ brand, model, category: '', q: '', sort: 'featured' }, undefined, routes.all))}" data-bike-brand="${esc(brand)}" data-bike-model="${esc(model)}"><span class="search-result-thumb">${photo ? `<img src="${esc(photo.src)}" alt="" loading="lazy" decoding="async">` : ''}</span><span class="search-result-copy"><strong>${markTerms(model, q)}</strong><span>${esc(brand)} · ${n ? `${n} ${n === 1 ? 'accessory' : 'accessories'}` : 'No accessories listed yet'}</span></span>${icon('arrow')}</a>`;
    }).join('') : '';
    const bikeBlock = bikeRows ? `<p class="search-label">Bikes</p><div class="search-results">${bikeRows}</div>` : '';
    const chips = `<div class="search-chips">${popularSearches.map(term => `<button type="button" class="search-chip" data-action="search-term" data-term="${esc(term)}">${esc(term)}</button>`).join('')}</div>`;
    let html;
    if (!q) {
      html = `<p class="search-label">Popular searches</p>${chips}<p class="search-label">Suggested accessories</p><div class="search-results">${list.slice(0, 4).map(row).join('')}</div>`;
    } else if (!list.length && !universal.length && bikeBlock) {
      html = bikeBlock;
    } else if (!list.length && !universal.length) {
      html = `<div class="search-empty">${icon('search')}<p><strong>No accessories match “${esc(q)}”</strong>Try a different name, or one of these:</p>${chips}</div>`;
    } else {
      const total = matchingProducts(context).length;
      html = `${bikeBlock}<p class="search-label">${total} matching ${total === 1 ? 'accessory' : 'accessories'}</p><div class="search-results">${list.map(row).join('')}</div>${universal.length ? `<p class="search-label">Universal options · Check fitment</p><div class="search-results">${universal.map(row).join('')}</div>` : ''}<button type="submit" form="search-form-el" class="search-see-all">See all results for “${esc(q)}” ${icon('arrow')}</button>`;
    }
    target.innerHTML = html;
    if (!reduceMotion()) target.querySelectorAll('.search-result,.search-chip,.search-see-all,.search-empty').forEach((el, i) => { el.style.animationDelay = `${Math.min(i, 8) * 28}ms`; el.classList.add('search-in'); });
  }

  /* Validation */
  function fieldError(field, message) {
    const errorId = field.getAttribute('aria-describedby');
    const error = errorId ? document.getElementById(errorId) : null;
    field.setAttribute('aria-invalid', message ? 'true' : 'false');
    if (error) { error.textContent = message; error.hidden = !message; }
  }
  function validQuantity(field) {
    const n = Number(field.value);
    const valid = Number.isInteger(n) && n >= 1 && n <= 99;
    fieldError(field, valid ? '' : 'Choose a whole quantity between 1 and 99.');
    return valid;
  }

  /* Rendering */
  function renderPage() {
    if (onShopPage) { if (!awaitingCatalogue) renderShop(); }
    else if (onBikesPage) renderBikes();
    else if (onProductPage) renderProductDynamic();
    else if (onCartPage) renderCart();
    else if (template === 'index') renderHome();
    updateHeader();
    if (!firstRender) {
      requestAnimationFrame(() => {
        let top = preserveScroll ?? 0;
        const anchor = document.querySelector('.shop-type-anchor');
        if (anchor && preserveScroll !== null) {
          const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || 0;
          top = Math.min(top, Math.max(0, anchor.getBoundingClientRect().top + window.scrollY - headerHeight));
        }
        window.scrollTo({ top, behavior: 'instant' });
        if (pendingFocus) document.getElementById(pendingFocus)?.focus({ preventScroll: true });
        else document.getElementById('app')?.focus({ preventScroll: true });
        pendingFocus = null; preserveScroll = null;
      });
    }
    firstRender = false;
  }

  document.addEventListener('focusout', e => {
    const field = e.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;
    if (field.closest('[data-form="bike"]') && field.required && !field.disabled) fieldError(field, field.value ? '' : 'Choose Honda or Royal Enfield.');
    if (field.name === 'quantity' && field.closest('[data-form="purchase"]')) validQuantity(field);
    requestAnimationFrame(syncPurchaseBar);
  });
  document.addEventListener('input', e => {
    if (e.target.id === 'search-input') searchSuggestions(e.target.value);
    if (e.target.id === 'bike-search') filterBikeCards(e.target.value);
  });
  document.addEventListener('submit', async e => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.dataset.form) return;
    e.preventDefault();
    const data = new FormData(form);
    if (form.dataset.form === 'bike') {
      const brand = form.elements.brand; const model = form.elements.model;
      fieldError(brand, brand.value ? '' : 'Choose Honda or Royal Enfield.');
      if (!brand.value) { brand.focus(); return; }
      if (model.value && !bikes[brand.value]?.includes(model.value)) { model.focus(); return; }
      setBike(brand.value, model.value);
      if (bikeReturnCart) {
        bikeReturnCart = false; closeDialogs(); renderPage(); openDialog('cart-dialog'); notify('Your bike has been saved.');
      } else if (onProductPage || onCartPage) {
        closeDialogs(); renderPage(); notify('Your bike has been saved.');
        if (onProductPage) document.getElementById('product-fitment-status')?.scrollIntoView({ block: 'center', behavior: 'instant' });
      } else if (!model.value) navigate(bikeSelectionURL(brand.value));
      else navigate(shopURL({ brand: brand.value, model: model.value, category: '', q: '', sort: 'featured' }));
      return;
    }
    if (form.dataset.form === 'search') {
      const q = String(data.get('q') || '').trim();
      navigate(shopURL({ q }, searchContext || browseContext(), onShopPage ? shopPath : routes.all));
      return;
    }
    if (form.dataset.form === 'collection-search') {
      navigate(shopURL({ q: String(data.get('q') || '').trim() }, getFilters()), { preserveScroll: true, focusId: 'results-title' });
      return;
    }
    if (form.dataset.form === 'purchase') {
      const p = productPage; if (!p) return;
      const v = p.variants.find(item => String(item.id) === String(data.get('id')));
      const field = form.elements.quantity;
      if (!validQuantity(field)) { field.scrollIntoView({ block: 'center', behavior: 'instant' }); field.focus(); return; }
      if (!v?.available) { notify('This option is currently unavailable.'); return; }
      const { selected: fitment, fit } = shopperFitment(p);
      const bikeLabel = productBikeName(fitment);
      const qty = Number(field.value);
      rememberPurchase();
      const existing = cart.items?.find(item => String(item.variant_id) === String(v.id) && item.properties?.Bike === bikeLabel);
      if (existing && existing.quantity + qty > 99) { notify('You can add up to 99 of each accessory.'); return; }
      const buttons = [document.getElementById('add-to-cart'), document.getElementById('mobile-add-to-cart')];
      buttons.forEach(button => { if (button) button.disabled = true; });
      try {
        const response = await fetch(cartURL(routes.cartAdd), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: v.id, quantity: qty, properties: { Bike: bikeLabel, _fit_kind: fit.kind, _fit_title: fit.title, _fit_text: fit.text } }] })
        });
        if (!response.ok) { const error = await response.json().catch(() => ({})); notify(error.description || 'This accessory could not be added to your cart.'); return; }
        await refreshCart();
        renderIsland(p.shortName);
      } catch {
        notify('This accessory could not be added to your cart. Check your connection and try again.');
      } finally {
        syncPurchaseControls();
      }
    }
  });
  document.addEventListener('change', async e => {
    const el = e.target;
    const form = el.closest('form');
    if (form?.dataset.form === 'bike' && el.name === 'brand') {
      const model = form.elements.model; model.disabled = !el.value;
      model.innerHTML = '<option value="">All models</option>' + (bikes[el.value] || []).map(m => `<option>${esc(m)}</option>`).join('');
      fieldError(el, ''); fieldError(model, '');
    }
    if (form?.dataset.form === 'purchase' && el.name === 'quantity') syncPurchaseControls();
    if (el.dataset.action === 'sort') navigate(shopURL({ sort: el.value }, getFilters()), { preserveScroll: true, focusId: 'sort' });
    if (el.dataset.action === 'variant' && productPage) {
      const v = productPage.variants.find(item => String(item.id) === el.value); if (!v) return;
      productState.variant = String(v.id);
      const swatchLabel = document.querySelector('[data-swatch-label]');
      if (swatchLabel && el.dataset.title) swatchLabel.textContent = el.dataset.title;
      if (Number.isInteger(v.imageIndex) && productPage.images[v.imageIndex]) selectGallery(productPage, v.imageIndex);
      syncPurchaseControls();
    }
    if (el.dataset.action === 'cart-quantity') {
      const item = cartItem(el.dataset.key); if (!item) return;
      const n = Number(el.value);
      if (!Number.isInteger(n) || n < 1 || n > 99) { el.value = item.quantity; notify('Choose a whole quantity between 1 and 99.'); return; }
      const scope = el.closest('dialog, [data-cart-page]') || document;
      await changeCartLine(item.key, n); renderCart(); scope.querySelector(`[data-cart-key="${CSS.escape(cartItem(el.dataset.key)?.key || el.dataset.key)}"] [data-action="cart-quantity"]`)?.focus();
    }
  });
  document.addEventListener('click', async e => {
    const control = e.target.closest('[data-action]');
    if (control && !(control.tagName === 'A' && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey))) {
      const action = control.dataset.action;
      const handled = () => { e.preventDefault(); };
      if (action === 'close') { closeDialog(control.closest('dialog')); return; }
      if (action === 'cart') { handled(); openDialog('cart-dialog'); return; }
      if (action === 'search') { handled(); openDialog('search-dialog'); return; }
      if (action === 'menu') { openDialog('menu-dialog'); return; }
      if (action === 'help') { openDialog('help-dialog'); return; }
      if (action === 'information') { openDialog('information-dialog'); return; }
      if (action === 'choose-bike') {
        if (document.getElementById('cart-dialog')?.open || onProductPage || onCartPage) openDialog('bike-dialog');
        else navigate(bikeSelectionURL(browseContext().brand || bike.brand));
        return;
      }
      if (action === 'home-showcase') {
        const brand = control.dataset.brand;
        if (brand !== '' && !Object.hasOwn(bikes, brand)) return;
        if (brand === showcaseBrand) return;
        showcaseBrand = brand;
        refreshHomeShowcase(control.id); return;
      }
      if (action === 'part-category') {
        navigate(shopURL({ category: control.dataset.category }, getFilters()), { preserveScroll: true, focusId: control.id }); return;
      }
      if (action === 'chip-scroll') {
        const row = control.parentElement.querySelector('.shop-chip-row');
        row.scrollBy({ left: Number(control.dataset.dir) * row.clientWidth * 0.75, behavior: reduceMotion() ? 'auto' : 'smooth' });
        return;
      }
      if (action === 'bike-search-clear') {
        const input = document.getElementById('bike-search');
        input.value = ''; filterBikeCards(''); input.focus(); return;
      }
      if (action === 'search-clear' || action === 'search-term') {
        const input = document.getElementById('search-input');
        input.value = action === 'search-term' ? control.dataset.term : '';
        searchSuggestions(input.value); input.focus(); return;
      }
      if (action === 'search-all-brands') {
        setSearchScope({ brand: '', model: '', category: '', q: '', sort: 'featured' });
        document.getElementById('search-input').focus(); return;
      }
      if (action === 'fit-pick') {
        const brand = control.dataset.brand, model = control.dataset.model;
        if (!bikes[brand]?.includes(model)) return;
        setBike(brand, model);
        renderProductDynamic();
        document.querySelector('[data-fitment-panel]')?.classList.add('anim-fade');
        document.querySelector('[data-fitment-panel] .pdp-fit-help')?.focus({ preventScroll: true });
        return;
      }
      if (action === 'quick-add') {
        const p = findProduct(control.dataset.product);
        if (!p || control.disabled) return;
        const { selected: fitment, fit } = shopperFitment(p);
        const bikeLabel = productBikeName(fitment);
        const existing = cart.items?.find(item => String(item.variant_id) === String(p.variantId) && item.properties?.Bike === bikeLabel);
        if (existing && existing.quantity >= 99) { notify('You can add up to 99 of each accessory.'); return; }
        control.disabled = true;
        control.classList.add('is-busy');
        try {
          const response = await fetch(cartURL(routes.cartAdd), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1, properties: { Bike: bikeLabel, _fit_kind: fit.kind, _fit_title: fit.title, _fit_text: fit.text } }] })
          });
          if (!response.ok) { const error = await response.json().catch(() => ({})); notify(error.description || 'This accessory could not be added to your cart.'); return; }
          await refreshCart();
          control.classList.add('is-added');
          control.innerHTML = `${icon('check')}<span class="card-add-label">Added</span>`;
          control.setAttribute('aria-label', `${p.shortName} added to cart`);
          renderIsland(p.shortName);
          setTimeout(() => {
            if (!control.isConnected) return;
            control.classList.remove('is-added');
            control.innerHTML = `${icon('bag-plus')}<span class="card-add-label">Add</span>`;
            control.setAttribute('aria-label', `Add ${p.shortName} to cart`);
          }, 2000);
        } catch {
          notify('This accessory could not be added to your cart. Check your connection and try again.');
        } finally {
          control.disabled = false;
          control.classList.remove('is-busy');
        }
        return;
      }
      if (action === 'gallery' && productPage) { selectGallery(productPage, Number(control.dataset.index)); return; }
      if (action === 'gallery-step' && productPage) { selectGallery(productPage, Math.max(0, Math.min(productPage.images.length - 1, productState.image + Number(control.dataset.step)))); return; }
      if (action === 'zoom' && productPage) {
        if (!productPage.images.length) return;
        const index = Number.isInteger(Number(control.dataset.index)) && control.dataset.index !== undefined ? Number(control.dataset.index) : productState.image;
        lightboxState = { index, zoomed: false }; paintLightbox(); openDialog('lightbox-dialog'); return;
      }
      if (action === 'lightbox-previous' || action === 'lightbox-next') { moveLightbox(action === 'lightbox-next' ? 1 : -1); return; }
      if (action === 'lightbox-zoom') { if (lightboxState) { lightboxState.zoomed = !lightboxState.zoomed; paintLightbox(); document.getElementById('lightbox-stage').focus(); } return; }
      if (action === 'product-info') {
        const content = document.getElementById('product-info-content');
        if (!content) return;
        document.querySelector('#product-info-dialog .dialog-body').replaceChildren(content.content.cloneNode(true));
        openDialog('product-info-dialog'); return;
      }
      if (action === 'quantity-plus' || action === 'quantity-minus') {
        const input = control.closest('.quantity-control').querySelector('input');
        input.value = Math.max(1, Math.min(99, (Number.parseInt(input.value, 10) || 1) + (action === 'quantity-plus' ? 1 : -1))); fieldError(input, ''); syncPurchaseControls(); return;
      }
      if (action === 'clear-filters') { navigate(shopURL({ category: '', q: '' }, getFilters()), { preserveScroll: true, focusId: 'results-title' }); return; }
      if (action === 'remove-filter') {
        const key = control.dataset.key; const updates = { [key]: '' }; if (key === 'brand') updates.model = '';
        navigate(shopURL(updates, getFilters()), { preserveScroll: true, focusId: 'results-title' }); return;
      }
      const key = control.dataset.key;
      if (action === 'cart-plus' || action === 'cart-minus') {
        const item = cartItem(key); if (!item) return;
        await changeCartLine(key, Math.max(1, Math.min(99, item.quantity + (action === 'cart-plus' ? 1 : -1))));
        const scope = control.closest('dialog, [data-cart-page]') || document;
        renderCart();
        scope.querySelector(`[data-cart-key="${CSS.escape(cartItem(key)?.key || key)}"] [data-action="${action}"]`)?.focus(); return;
      }
      if (action === 'remove-item') {
        const item = cartItem(key); if (!item) return;
        lastRemoved = { id: item.variant_id, quantity: item.quantity, properties: item.properties || {} };
        const name = findProduct(item.handle)?.shortName || item.product_title;
        const line = control.closest('.cart-line');
        if (line && !reduceMotion()) { line.classList.add('is-removing'); await new Promise(r => setTimeout(r, 220)); }
        await changeCartLine(key, 0);
        renderCart();
        (control.closest('dialog') ? document.getElementById('cart-title') : document.querySelector('[data-cart-page] h1'))?.focus();
        notify(`${name} removed.`, false, true);
        return;
      }
      if (action === 'cart-undo') {
        if (!lastRemoved) return;
        const restore = lastRemoved; lastRemoved = null;
        const response = await fetch(cartURL(routes.cartAdd), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [restore] }) });
        if (!response.ok) { notify('That accessory could not be restored.'); return; }
        await refreshCart(); cartChanged = true; renderCart();
        document.getElementById('toast').hidden = true;
        return;
      }
    }
    const link = e.target.closest('a[href]');
    if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.target === '_blank') return;
    let url;
    try { url = new URL(link.href, location.href); } catch { return; }
    if (url.origin !== location.origin) return;
    if (link.dataset.bikeModel) {
      const brand = link.dataset.bikeBrand, model = link.dataset.bikeModel;
      if (bikes[brand]?.includes(model)) setBike(brand, model);
    }
    if (url.pathname.includes('/products/')) {
      const context = link.closest('[data-browse-shop]')?.dataset.browseShop;
      if (context) rememberShop(context);
      else if (onShopPage) rememberShop(currentPath());
      else if (!onProductPage) rememberShop(shopURL({}, browseContext(), routes.all));
      return;
    }
    if (url.pathname === location.pathname && ((onShopPage && url.searchParams.get('view') !== 'bikes') || (onBikesPage && url.searchParams.get('view') === 'bikes')) && url.hash === '') {
      e.preventDefault();
      navigate(url.href);
    }
  });
  document.addEventListener('click', e => {
    const link = e.target.closest('a[href]');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
    let url; try { url = new URL(link.href, location.href); } catch { return; }
    if (url.origin !== location.origin || (url.pathname === location.pathname && url.search === location.search)) return;
    startNavProgress();
  });
  document.addEventListener('focusin', syncPurchaseBar);
  document.addEventListener('keydown', e => {
    if (e.target.id !== 'bike-search' || e.key !== 'Enter') return;
    const first = document.querySelector('#model-choice-grid .bike-model-card:not([hidden])');
    if (first) { e.preventDefault(); first.click(); }
  });
  document.getElementById('search-dialog')?.addEventListener('keydown', e => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...document.querySelectorAll('#search-suggestions .search-result, #search-suggestions .search-see-all')];
    if (!items.length) return;
    const input = document.getElementById('search-input');
    const i = items.indexOf(document.activeElement);
    e.preventDefault();
    if (e.key === 'ArrowDown') (items[i + 1] || items[0]).focus();
    else if (i <= 0) input.focus();
    else items[i - 1].focus();
  });
  document.addEventListener('keydown', e => {
    if (!document.getElementById('lightbox-dialog')?.open || lightboxState?.zoomed || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); moveLightbox(e.key === 'ArrowRight' ? 1 : -1); }
  });
  window.addEventListener('resize', syncPurchaseBar, { passive: true });
  window.visualViewport?.addEventListener('resize', syncPurchaseBar, { passive: true });
  window.addEventListener('scroll', syncPurchaseBar, { passive: true });
  window.addEventListener('popstate', () => { closeDialogs(); if (onShopPage || onBikesPage) { preserveScroll = null; renderPage(); } });
  window.addEventListener('pageshow', event => { if (event.persisted) refreshCart().catch(() => {}); });

  /* Custom dropdowns: a styled button and listbox drawn over each native select, which stays in the
     form (hidden) so values, change events and validation keep working. */
  const brandLogos = config.brandLogos || {};
  function selectOptionImage(select, option) {
    if (!option.value) return null;
    if (select.name === 'model') {
      const brand = select.form?.elements.brand?.value || bike.brand;
      const photo = bikePhoto(brand, option.value);
      return photo ? { src: photo.src, kind: 'bike' } : null;
    }
    if (select.name === 'brand') return brandLogos[option.value] ? { src: brandLogos[option.value], kind: 'logo' } : null;
    if (select.dataset.action === 'variant' && productPage) {
      const v = productPage.variants.find(item => String(item.id) === option.value);
      const image = v && Number.isInteger(v.imageIndex) ? productPage.images[v.imageIndex] : null;
      return image ? { src: image.src, kind: 'photo' } : null;
    }
    return null;
  }
  const selectThumb = image => image ? `<span class="mg-select-thumb is-${image.kind}"><img src="${esc(image.src)}" alt="" loading="lazy" decoding="async"></span>` : '';
  let selectCount = 0;
  let openSelect = null;
  function closeCustomSelect(focusButton) {
    if (!openSelect) return;
    const { wrap } = openSelect;
    wrap.classList.remove('is-open');
    wrap.querySelector('.mg-select-button').setAttribute('aria-expanded', 'false');
    const list = wrap.querySelector('.mg-select-list');
    if (list.matches(':popover-open')) list.hidePopover();
    list.hidden = true;
    if (focusButton) wrap.querySelector('.mg-select-button').focus();
    openSelect = null;
  }
  function paintCustomSelect(wrap) {
    const select = wrap.querySelector('select');
    const button = wrap.querySelector('.mg-select-button');
    const option = select.options[select.selectedIndex];
    const label = option ? option.textContent : '';
    const image = option ? selectOptionImage(select, option) : null;
    button.querySelector('.mg-select-value').innerHTML = `${selectThumb(image)}<span class="mg-select-text${option && !option.value ? ' is-placeholder' : ''}">${esc(label)}</span>`;
    button.disabled = select.disabled;
    button.setAttribute('aria-invalid', select.getAttribute('aria-invalid') || 'false');
    const describedBy = select.getAttribute('aria-describedby');
    if (describedBy) button.setAttribute('aria-describedby', describedBy);
  }
  function renderCustomOptions(wrap) {
    const select = wrap.querySelector('select');
    const list = wrap.querySelector('.mg-select-list');
    list.innerHTML = [...select.options].map((option, i) => {
      const selected = i === select.selectedIndex;
      return `<li role="option" id="${list.id}-${i}" class="mg-select-option${option.value ? '' : ' is-placeholder'}" data-index="${i}" aria-selected="${selected}"${option.disabled ? ' aria-disabled="true"' : ''}>${selectThumb(selectOptionImage(select, option))}<span class="mg-select-text">${esc(option.textContent)}</span>${selected ? icon('check') : ''}</li>`;
    }).join('');
  }
  function setActiveOption(wrap, index) {
    const list = wrap.querySelector('.mg-select-list');
    const items = [...list.children];
    if (!items.length) return;
    const i = Math.max(0, Math.min(items.length - 1, index));
    items.forEach(item => item.classList.toggle('is-active', Number(item.dataset.index) === i));
    list.setAttribute('aria-activedescendant', items[i].id);
    items[i].scrollIntoView({ block: 'nearest' });
    openSelect.active = i;
  }
  const supportsPopover = typeof HTMLElement.prototype.showPopover === 'function';
  // The list sits in the top layer (popover) so dialogs and scroll areas never clip it; place it under the button, or above when there is no room.
  function positionCustomList(wrap) {
    const list = wrap.querySelector('.mg-select-list');
    const rect = wrap.querySelector('.mg-select-button').getBoundingClientRect();
    const viewHeight = window.visualViewport?.height || innerHeight;
    const want = Math.min(list.scrollHeight, 320);
    const roomBelow = viewHeight - rect.bottom - 12;
    const up = roomBelow < want && rect.top > roomBelow;
    wrap.classList.toggle('opens-up', up);
    if (!supportsPopover) return;
    const width = Math.max(rect.width, 200);
    list.style.left = `${Math.min(rect.left, innerWidth - width - 8)}px`;
    list.style.width = `${width}px`;
    list.style.maxHeight = `${Math.max(160, Math.min(320, (up ? rect.top : roomBelow) - 12))}px`;
    list.style.top = up ? 'auto' : `${rect.bottom + 6}px`;
    list.style.bottom = up ? `${viewHeight - rect.top + 6}px` : 'auto';
  }
  function openCustomSelect(wrap) {
    if (openSelect?.wrap === wrap) return;
    closeCustomSelect(false);
    const select = wrap.querySelector('select');
    const list = wrap.querySelector('.mg-select-list');
    renderCustomOptions(wrap);
    list.hidden = false;
    if (supportsPopover) list.showPopover();
    positionCustomList(wrap);
    wrap.classList.add('is-open');
    wrap.querySelector('.mg-select-button').setAttribute('aria-expanded', 'true');
    openSelect = { wrap, active: select.selectedIndex };
    setActiveOption(wrap, Math.max(0, select.selectedIndex));
    list.focus({ preventScroll: true });
  }
  function chooseCustomOption(wrap, index) {
    const select = wrap.querySelector('select');
    const option = select.options[index];
    if (!option || option.disabled) return;
    const changed = select.selectedIndex !== index;
    select.selectedIndex = index;
    closeCustomSelect(true);
    paintCustomSelect(wrap);
    if (changed) { select.dispatchEvent(new Event('input', { bubbles: true })); select.dispatchEvent(new Event('change', { bubbles: true })); }
  }
  function enhanceSelect(select) {
    if (select.dataset.enhanced || select.multiple || select.size > 1) return;
    select.dataset.enhanced = 'true';
    const id = `mg-select-${++selectCount}`;
    const wrap = document.createElement('div');
    wrap.className = 'mg-select';
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    const label = select.id ? document.querySelector(`label[for="${CSS.escape(select.id)}"]`) : null;
    if (label && !label.id) label.id = `${id}-label`;
    wrap.insertAdjacentHTML('beforeend', `<button type="button" class="mg-select-button ${esc(select.className)}" aria-haspopup="listbox" aria-expanded="false" aria-controls="${id}-list"${label ? ` aria-labelledby="${label.id} ${id}-value"` : ''}><span class="mg-select-value" id="${id}-value"></span>${icon('chevron')}</button><ul class="mg-select-list" id="${id}-list" role="listbox" tabindex="-1"${supportsPopover ? ' popover="manual"' : ''}${label ? ` aria-labelledby="${label.id}"` : ''} hidden></ul>`);
    label?.addEventListener('click', e => { e.preventDefault(); wrap.querySelector('.mg-select-button').focus(); });
    select.addEventListener('focus', () => wrap.querySelector('.mg-select-button').focus());
    select.addEventListener('change', () => paintCustomSelect(wrap));
    new MutationObserver(() => { paintCustomSelect(wrap); if (openSelect?.wrap === wrap) renderCustomOptions(wrap); })
      .observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-invalid', 'aria-describedby'] });
    paintCustomSelect(wrap);
  }
  const enhanceSelects = (root = document) => root.querySelectorAll('select:not([data-enhanced])').forEach(enhanceSelect);
  new MutationObserver(records => {
    if (records.some(r => [...r.addedNodes].some(n => n.nodeType === 1 && (n.matches('select') || n.querySelector('select'))))) enhanceSelects();
  }).observe(document.body, { childList: true, subtree: true });
  // A brand change rebuilds the model options, so repaint dependent pickers (their bike photos change).
  document.addEventListener('change', e => { if (e.target.name === 'brand') e.target.form?.querySelectorAll('select[name="model"]').forEach(s => s.closest('.mg-select') && paintCustomSelect(s.closest('.mg-select'))); });
  document.addEventListener('click', e => {
    const button = e.target.closest('.mg-select-button');
    if (button) { const wrap = button.closest('.mg-select'); if (openSelect?.wrap === wrap) closeCustomSelect(true); else openCustomSelect(wrap); return; }
    const item = e.target.closest('.mg-select-option');
    if (item) { chooseCustomOption(item.closest('.mg-select'), Number(item.dataset.index)); return; }
    if (openSelect && !e.target.closest('.mg-select')) closeCustomSelect(false);
  });
  let typeahead = '', typeaheadTimer = null;
  document.addEventListener('keydown', e => {
    const button = e.target.closest?.('.mg-select-button');
    if (button && !openSelect && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); openCustomSelect(button.closest('.mg-select')); return; }
    if (!openSelect || !e.target.closest?.('.mg-select')) return;
    const { wrap } = openSelect;
    const select = wrap.querySelector('select');
    const last = select.options.length - 1;
    const step = dir => { let i = openSelect.active; do { i += dir; } while (i >= 0 && i <= last && select.options[i].disabled); if (i >= 0 && i <= last) setActiveOption(wrap, i); };
    if (e.key === 'ArrowDown') { e.preventDefault(); step(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); step(-1); }
    else if (e.key === 'Home') { e.preventDefault(); setActiveOption(wrap, 0); }
    else if (e.key === 'End') { e.preventDefault(); setActiveOption(wrap, last); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); chooseCustomOption(wrap, openSelect.active); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeCustomSelect(true); }
    else if (e.key === 'Tab') closeCustomSelect(false);
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      typeahead += e.key.toLowerCase(); clearTimeout(typeaheadTimer); typeaheadTimer = setTimeout(() => { typeahead = ''; }, 600);
      const match = [...select.options].findIndex(o => !o.disabled && o.textContent.toLowerCase().startsWith(typeahead));
      if (match >= 0) setActiveOption(wrap, match);
    }
  }, true);
  window.addEventListener('resize', () => closeCustomSelect(false), { passive: true });
  document.addEventListener('scroll', e => { if (openSelect && !openSelect.wrap.contains(e.target)) closeCustomSelect(false); }, { capture: true, passive: true });

  /* Full catalogue: Liquid lists at most 250 products per page, so any further products are fetched
     from the collection.catalogue template and merged in, then the parts that count or list them refresh. */
  // Liquid lists at most 250 products per page, so the rest of the catalogue (and of the current collection) loads from ?view=catalogue pages.
  const cataloguePages = new Map();
  function cataloguePage(url, page) {
    const key = `${url}&page=${page}`;
    if (!cataloguePages.has(key)) cataloguePages.set(key, fetch(key).then(response => response.ok ? response.json() : []).catch(() => []));
    return cataloguePages.get(key);
  }
  async function loadFullCatalogue() {
    const known = new Set(products.map(p => p.id));
    const add = items => items.forEach(item => { if (!known.has(item.id)) { known.add(item.id); products.push(toProduct(item)); } });
    let changed = false;
    const total = config.productsTotal || 0;
    if (config.catalogueUrl && products.length < total) {
      const order = [...Array(Math.ceil(total / 250)).keys()].map(i => i + 1).filter(n => n !== 1).concat(1);
      for (const page of order) {
        if (products.length >= total) break;
        add(await cataloguePage(config.catalogueUrl, page));
      }
      changed = true;
    }
    const count = Number(shopRoot?.dataset.collectionCount || 0);
    if (collectionHandles && shopRoot.dataset.catalogueUrl && collectionHandles.length < count) {
      const listed = new Set(collectionHandles);
      for (let page = 2; page <= Math.ceil(count / 250); page++) {
        const items = await cataloguePage(shopRoot.dataset.catalogueUrl, page);
        add(items);
        items.forEach(item => { if (!listed.has(item.id)) { listed.add(item.id); collectionHandles.push(item.id); } });
      }
      collectionPool = collectionHandles.map(findProduct).filter(Boolean);
      changed = true;
    }
    if (changed) addTaggedBikes();
    return changed;
  }
  function refreshHomeCounts() {
    if (showcaseRoot) {
      const listed = new Set(showcaseOrder.map(p => p.id));
      products.forEach(p => { if (!listed.has(p.id)) showcaseOrder.push(p); });
      showcaseRoot.querySelectorAll('.showcase-choice').forEach(button => {
        const count = button.querySelector('.showcase-count');
        if (count) count.textContent = showcaseProducts(button.dataset.brand || '').length;
      });
      const status = document.getElementById('showcase-status');
      const brand = showcaseRoot.querySelector('.showcase-choice[aria-pressed="true"]')?.dataset.brand || '';
      if (status) status.textContent = `Top ${Math.min(showcasePageSize, showcaseProducts(brand).length)} of ${showcaseProducts(brand).length} accessories${brand ? ' for ' + brand : ''}`;
    }
    if (bike.brand) { homeCategories(); return; }
    document.querySelectorAll('.part-type-grid .part-type').forEach((card, i) => {
      const n = products.filter(p => p.category === categories[i]).length;
      const count = card.querySelector('.part-type-count');
      if (count) count.textContent = n ? `${n} ${n === 1 ? 'accessory' : 'accessories'}` : 'No accessories yet';
    });
  }
  // Redraw a region but keep product cards that are already on screen, so their photo and fade-in carry on instead of starting again.
  function redraw(root, html) {
    const keep = new Map([...root.querySelectorAll('[data-card-id]')].map(el => [`${el.dataset.cardKind}|${el.dataset.cardId}`, el]));
    const next = document.createElement('div');
    next.innerHTML = html;
    next.querySelectorAll('[data-card-id]').forEach(el => {
      const key = `${el.dataset.cardKind}|${el.dataset.cardId}`;
      const prev = keep.get(key);
      if (prev) { keep.delete(key); el.replaceWith(prev); }
    });
    root.replaceChildren(...next.childNodes);
  }
  // The rest of the catalogue arrives just after load. Redraw quietly: rebuilt cards keep their place without replaying the entrance animation.
  function quiet(root) {
    if (!root || !motionOn()) return;
    [root, ...root.querySelectorAll('*')].forEach(el => { if (getComputedStyle(el).animationName !== 'none') el.style.animation = 'none'; });
  }
  // Update the bike cards' part counts in place; rebuild only when the list of models itself changed.
  function refreshBikeCounts() {
    const root = document.querySelector('[data-bikes-root]');
    const grid = document.getElementById('model-choice-grid');
    const brand = grid?.dataset.brand;
    const cards = grid ? [...grid.querySelectorAll('.bike-model-card')] : [];
    if (!grid || cards.length !== (bikes[brand] || []).length) { renderBikes(); quiet(root); return; }
    cards.forEach((card, i) => {
      const n = products.filter(p => p.brand === brand && p.models.includes(card.dataset.bikeModel)).length;
      const count = document.getElementById(`bike-count-${i}`);
      if (count) count.textContent = n ? `${n} ${n === 1 ? 'accessory' : 'accessories'} listed` : 'No accessories listed yet';
    });
  }
  function afterCatalogue() {
    if (onShopPage) { awaitingCatalogue = false; renderShop(true); quiet(shopRoot); }
    else if (onBikesPage) refreshBikeCounts();
    else if (template === 'index') refreshHomeCounts();
    else if (onProductPage && productPage) {
      const related = document.querySelector('[data-related-root]');
      const html = relatedProducts(productPage);
      if (related && related.innerHTML !== html) { redraw(related, html); quiet(related); }
    }
    if (document.getElementById('search-dialog')?.open) searchSuggestions(document.getElementById('search-input').value);
  }

  if (onProductPage) body.classList.add('on-product');
  enhanceSelects();
  // Collection pages wait for the rest of the catalogue so the list is drawn once, in its final order (the server list stays hidden meanwhile).
  let awaitingCatalogue = onShopPage && ((config.catalogueUrl && products.length < (config.productsTotal || 0)) || (Boolean(collectionHandles) && collectionHandles.length < Number(shopRoot?.dataset.collectionCount || 0)));
  renderPage();
  loadFullCatalogue().then(changed => { if (changed) afterCatalogue(); });
  syncSegments();
  observeReveal();
  window.addEventListener('resize', () => { syncSegments(); document.querySelectorAll('.chip-scroller').forEach(syncChipScroller); }, { passive: true });
  document.addEventListener('scroll', e => { const scroller = e.target.closest?.('.chip-scroller'); if (scroller) syncChipScroller(scroller); }, { capture: true, passive: true });
  if (onProductPage) { setupPurchaseBar(); setupGallery(); }
})();
